import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Input,
  DatePicker,
  Button,
  Popconfirm,
  Tag,
  App,
  Alert,
  Spin,
  Select,
  Checkbox,
  Table,
  Drawer,
  Divider,
  Space,
  Popover,
  Grid,
  Dropdown,
} from 'antd';
// Hint tooltips: 1s show delay + auto-hide after 5s (see HintTooltip).
import { HintTooltip as Tooltip } from '../components/HintTooltip';
import type { ColumnsType } from 'antd/es/table';
import {
  ReloadOutlined,
  CopyOutlined,
  DownloadOutlined,
  UploadOutlined,
  EyeOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  HistoryOutlined,
  HolderOutlined,
  ThunderboltOutlined,
  UndoOutlined,
  CloseOutlined,
  EditOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
  SafetyCertificateOutlined,
  DownOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { QuotaBanner } from '../QuotaBanner';
import { api, User, HwrsvFields, isAdminRole } from '../api';
import { t } from '../i18n';
import { BackupHistoryModal } from './BackupHistoryModal';
import { HexViewer } from './HexViewer';
import {
  parseBarcode,
  buildBarcode,
  dateCodeFromManufactured,
  decodeDateCode,
  incrementSerial,
  validateBarcode,
} from '../barcode';
import { downloadXlsx, stampNow, XlsxRecord, XlsxSheet } from '../xlsx';
import { fmtDateTimeMin } from '../time';

type EditableField =
  | 'bom'
  | 'level'
  | 'barcode'        // direct value — only in manual mode
  | 'barcodeSerial'  // counter-assigned serial — feeds the derived barcode
  | 'model'
  | 'description'
  | 'manufactured'
  | 'boardType'
  | 'wlType'         // disk whitelist: list product type (NOT an elabel field)
  | 'wlVersion';     // disk whitelist: list version

/** Fields that map 1:1 to a disk-summary column (excludes the assembled BarCode). */
type PlainField = 'bom' | 'level' | 'manufactured' | 'boardType' | 'model' | 'description';

type Edits = Record<string, Partial<Record<EditableField, string>>>;

/**
 * One reversible mutation for the undo history stack. `field`/`prev` capture the
 * previous value of an `edits[serial][field]` slot (prev === undefined → the
 * field wasn't set). `manualPrev`/`syncPrev` capture the previous membership of
 * the manualBc / syncDate sets for ops (manual/sync toggles) that flip them.
 */
type UndoEntry = {
  serial: string;
  field?: EditableField;
  prev?: string;
  manualPrev?: boolean;
  syncPrev?: boolean;
  bomSyncPrev?: boolean;
};

interface DiskSummaryShape {
  bom: string;
  level: string;
  barcode: string | null;
  model: string | null;
  description: string | null;
  manufactured: string | null;
  boardType: string | null;
}

/**
 * The barcode is assembled, not typed: material ← BOM (8 chars), the two
 * date chars ← Manufactured, serial ← the batch counter. Everything else
 * (snId, vendor, RoHS, internal model) is kept from the disk's own barcode.
 *
 * The date is only recomputed from Manufactured when `syncDate` is on. Many
 * disks encode a BarCode date that differs from their Manufactured field, so
 * an unconditional recompute produced a spurious old→new diff on every row.
 * Default off → the stored BarCode date is kept verbatim.
 */
function deriveBarcode(
  sum: DiskSummaryShape | null,
  ed: Edits[string],
  syncDate: boolean,
  syncBom: boolean,
): string {
  const tmpl = sum?.barcode ?? '';
  if (!tmpl) return '';
  const bc = parseBarcode(tmpl);
  // Material ← BOM only when opted in (per-disk `sync BOM`). Like the date, many
  // disks carry a BarCode material that legitimately differs from their BOM
  // field, so an unconditional recompute produced a spurious old→new diff on
  // every such row. Default off → the stored BarCode material is kept verbatim.
  if (syncBom) {
    const bom = ed.bom ?? sum?.bom ?? '';
    // The barcode material slot is EXACTLY 8 chars, so only a valid 8-char BOM is
    // written. A wrong-length BOM is left un-synced (would corrupt the fixed
    // layout) — the BOM cell shows a light ⚠ so the user knows why (see bomCol).
    if (bom.length === 8) bc.material = bom;
  }
  if (syncDate) {
    const manufactured = ed.manufactured ?? sum?.manufactured ?? '';
    const dc = manufactured ? dateCodeFromManufactured(manufactured) : null;
    if (dc) bc.dateCode = dc;
  }
  if (ed.barcodeSerial) bc.serial = ed.barcodeSerial;
  return buildBarcode(bc);
}

/**
 * Open a native file picker for a single .hwrsv, resolving the chosen file (or
 * null if cancelled). Cancel is detected via the `cancel` event and a
 * window-focus fallback for browsers that don't fire it — so the awaiting
 * caller never hangs on a pending promise when the dialog is dismissed.
 */
function pickHwrsvFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.hwrsv,application/octet-stream';
    let settled = false;
    const settle = (f: File | null): void => {
      if (settled) return;
      settled = true;
      window.removeEventListener('focus', onFocus);
      resolve(f);
    };
    const onFocus = (): void => {
      // Fires when the dialog closes; defer so a real selection's change event
      // wins the race. If nothing was picked, treat it as a cancel.
      setTimeout(() => settle(input.files?.[0] ?? null), 300);
    };
    input.onchange = () => settle(input.files?.[0] ?? null);
    input.oncancel = () => settle(null);
    window.addEventListener('focus', onFocus, { once: true });
    input.click();
  });
}

/**
 * Antd Tag preset-colour → CSS hex, for the small type dot in the Serial cell.
 * Keeps the dot exactly consistent with the colour of the Type-column Tag.
 */
const TYPE_DOT: Record<string, string> = {
  geekblue: '#2f54eb', // NVMe
  green: '#52c41a', // SAS 2.5"
  orange: '#fa8c16', // SAS 3.5"
  gold: '#faad14', // SAS (generic)
};

export function SessionEditor({
  user,
  sessionId,
  onSessionChange,
  onDirtyChange,
  leftPanel,
}: {
  user: User;
  // When embedded (Disks workspace) the session id is passed as a prop; the
  // route param is the fallback for the standalone /sessions/:id deep link.
  sessionId?: string;
  // Embedded mode: instead of routing to a freshly-produced session (reload /
  // power-cycle / post-apply refresh), swap it in place within the workspace.
  onSessionChange?: (id: string) => void;
  // Report whether there are unsaved edits, so the embedding workspace can warn
  // before switching to another device.
  onDirtyChange?: (dirty: boolean) => void;
  // Optional panel (the device picker) rendered to the LEFT of the header
  // toolbar in one 40/60 row; the disk table below stays full width.
  leftPanel?: ReactNode;
}) {
  const params = useParams<{ id: string }>();
  const id = sessionId ?? params.id;
  const navigate = useNavigate();
  // On a narrow screen the pinned columns (selection+order+serial ≈ 302px left +
  // actions 148px right) exceed the viewport, leaving no room to scroll the middle
  // columns — so below `md` we drop `fixed` entirely and let the whole table scroll.
  const screens = Grid.useBreakpoint();
  const isNarrow = !screens.md;
  // Route to the fresh session, or (embedded) hand it back to the workspace.
  const goToSession = (sid: string) => {
    if (onSessionChange) onSessionChange(sid);
    else navigate(`/sessions/${sid}`);
  };
  const qc = useQueryClient();
  const { message, modal } = App.useApp();
  // Registered certificate authorities (built-in "Our" + generated clones) — feeds
  // the per-disk "change certificate" menu. Admin-only.
  const caListQ = useQuery({
    queryKey: ['cert-authorities'],
    queryFn: api.listCertAuthorities,
    enabled: isAdminRole(user.role),
    staleTime: 60_000,
  });
  const signableCas = caListQ.data?.authorities.filter((c) => c.canSign) ?? [];
  const [reloading, setReloading] = useState(false);
  // Non-null (disk serials) while a power-cycle request is in flight and we wait
  // for the disks to reboot and re-inventory (which produces a fresh session we
  // navigate to).
  const [powercycling, setPowercycling] = useState<string[] | null>(null);
  // Cleared on unmount so the long poll loops below (reload / power-cycle) bail
  // before calling navigate()/setState on a component that's already gone.
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);
  const [edits, setEdits] = useState<Edits>({});
  // Chronological undo stack: every mutation of `edits` (and the manual/sync
  // toggles) pushes an entry BEFORE it applies, so we can peel changes back.
  const [history, setHistory] = useState<UndoEntry[]>([]);
  const [manualBc, setManualBc] = useState<Set<string>>(new Set());
  // Per-disk: recompute the BarCode date from Manufactured. The set starts empty
  // but every disk is SEEDED ON by the default-ON effect below (syncSeen), so a
  // user can untick individual disks; this is just the initial (pre-seed) value.
  const [syncDate, setSyncDate] = useState<Set<string>>(new Set());
  // Per-disk: recompute the BarCode material (8-char) from the BOM field. Also
  // seeded ON by default (mirrors syncDate); the empty init is pre-seed only.
  const [syncBom, setSyncBom] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Per-disk raw DISK_AUTHENTICATION (r4/r5, base64) staged from a .hwrsv import —
  // a flashable cert change that has no editable field. Keyed by serial.
  const [authEdits, setAuthEdits] = useState<Record<string, { r4: string; r5: string }>>({});
  // Direction of a staged certificate conversion (r4/r5 in authEdits), per serial:
  // 'our' = re-issued onto our OEM CA, 'orig' = restored to the factory cert.
  // A staged certificate change per serial: either converting to one of our CAs
  // (dir 'ca' + which CA), or restoring the original from a backup (dir 'orig' +
  // which backup). The r4/r5 bytes live in authEdits; this holds the display +
  // the caId for the usage tally.
  type StagedCert =
    | { dir: 'ca'; caId: string; caName: string }
    | { dir: 'orig'; source: string; at: number };
  const [certStaged, setCertStaged] = useState<Record<string, StagedCert>>({});
  // Per-field opt-out of flashing: keys `${serial}::${fieldId}`. A field listed
  // here is still shown (edited/derived) but NOT written — the operator unchecked
  // it in the confirm dialog or in the table. fieldIds: bom/level/barcode/model/
  // description/manufactured/boardType/wlType/wlVersion/wlBom/cert.
  const [excludedFields, setExcludedFields] = useState<Set<string>>(new Set());
  const exKey = (sn: string, fid: string): string => `${sn}::${fid}`;
  const isFieldExcluded = (sn: string, fid: string): boolean => excludedFields.has(exKey(sn, fid));
  const toggleFieldExcluded = (sn: string, fid: string): void =>
    setExcludedFields((s) => {
      const n = new Set(s);
      const k = exKey(sn, fid);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  const [batchMsg, setBatchMsg] = useState<string | null>(null);
  const [bcStart, setBcStart] = useState('');
  const [bcStartTouched, setBcStartTouched] = useState(false);
  const [bulkField, setBulkField] = useState<PlainField>('bom');
  const [bulkValue, setBulkValue] = useState('');
  const [order, setOrder] = useState<string[]>([]);
  const [sortField, setSortField] = useState<'manual' | 'slot' | 'id' | 'sn' | 'barcode'>('slot');
  const [sortDesc, setSortDesc] = useState(false);
  const [historyForSn, setHistoryForSn] = useState<string | null>(null);
  const [hexForSn, setHexForSn] = useState<string | null>(null);
  const [applyMsg, setApplyMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // Sticky success banner: captured at flash-confirm time so it SURVIVES the
  // automatic jump to the re-inventoried session. Dismissed only by the operator
  // (the Alert's close ×), never by the rescan.
  const [flashDone, setFlashDone] = useState<{
    results: Array<{ serial: string; status: string; detail?: string; powercycle?: string; returnedSec?: number }>;
    diskCount: number;
    sessionId: string;
  } | null>(null);
  // True while the post-apply auto-jump is polling for the fresh re-inventory
  // session. Shown as a "waiting for the disks to come back" note so the operator
  // doesn't manually reload into a still-rebooting disk (see the post-apply effect).
  const [postApplyWaiting, setPostApplyWaiting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewResult, setPreviewResult] = useState<{
    disks: Array<{
      serial: string;
      totalBytesChanged: number;
      regions: Array<{ id: number; name: string; bytesChanged: number; firstChangeOffset: number }>;
      outcome: { applied: string[]; rejected: { change: string; reason: string }[] };
    }>;
  } | null>(null);

  // --- Undo -----------------------------------------------------------------
  // Revert a single history entry: restore the edit slot to `prev` (or delete it
  // when prev is undefined/empty) and restore any manual/sync set membership.
  const applyUndo = (entry: UndoEntry): void => {
    const { serial, field, prev, manualPrev, syncPrev, bomSyncPrev } = entry;
    if (field !== undefined) {
      setEdits((prevEdits) => {
        const next = { ...prevEdits };
        const cur = { ...(next[serial] ?? {}) };
        if (prev === undefined || prev.length === 0) delete cur[field];
        else cur[field] = prev;
        if (Object.keys(cur).length === 0) delete next[serial];
        else next[serial] = cur;
        return next;
      });
    }
    if (manualPrev !== undefined) {
      setManualBc((s2) => {
        const n = new Set(s2);
        if (manualPrev) n.add(serial);
        else n.delete(serial);
        return n;
      });
    }
    if (syncPrev !== undefined) {
      setSyncDate((s2) => {
        const n = new Set(s2);
        if (syncPrev) n.add(serial);
        else n.delete(serial);
        return n;
      });
    }
    if (bomSyncPrev !== undefined) {
      setSyncBom((s2) => {
        const n = new Set(s2);
        if (bomSyncPrev) n.add(serial);
        else n.delete(serial);
        return n;
      });
    }
  };

  // Per-row undo: revert THAT disk's most recent change (repeatable).
  const undoForSerial = (sn: string): void => {
    let idx = -1;
    for (let i = history.length - 1; i >= 0; i--) {
      if (history[i]!.serial === sn) {
        idx = i;
        break;
      }
    }
    if (idx < 0) return;
    applyUndo(history[idx]!);
    setHistory((h) => h.filter((_, i) => i !== idx));
  };

  // Global undo (toolbar button + Ctrl+Z): revert the latest change overall.
  const undoGlobal = (): void => {
    if (history.length === 0) return;
    applyUndo(history[history.length - 1]!);
    setHistory((h) => h.slice(0, -1));
  };
  // Ref keeps the keydown listener (bound once) pointing at the latest closure.
  const undoGlobalRef = useRef<() => void>(() => {});
  undoGlobalRef.current = undoGlobal;

  // Ctrl/Cmd+Z outside a text field → global undo. Inside an <input>/<textarea>/
  // contenteditable we do nothing so the browser's native text undo still works.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
        const el = document.activeElement as HTMLElement | null;
        const tag = el?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable) return;
        e.preventDefault();
        undoGlobalRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Serials that have at least one undoable change (gates the per-row buttons).
  const undoableSerials = useMemo(() => new Set(history.map((h) => h.serial)), [history]);

  const sessionQ = useQuery({
    queryKey: ['session', id],
    queryFn: () => api.getSession(id!),
    enabled: !!id,
    refetchInterval: 3000,
  });

  // Resolve the device's display name (label, falling back to short id).
  const devicesQ = useQuery({
    queryKey: isAdminRole(user.role) ? ['devices'] : ['my-devices'],
    queryFn: isAdminRole(user.role) ? api.listDevices : api.listMyDevices,
    staleTime: 30_000,
  });
  // Clients consume quota per apply — block flashing when it's exhausted.
  const quotaQ = useQuery({
    queryKey: ['my-quota'],
    queryFn: () => api.myQuota(),
    enabled: user.role === 'client',
    refetchInterval: 10_000,
  });

  // Soft presence: tell the server I have THIS device's editor open (heartbeat),
  // so it shows up in the device lists for other operators. TTL-expired server-side.
  const editorDeviceId = sessionQ.data?.deviceId;
  useEffect(() => {
    if (!editorDeviceId) return;
    const ping = () => { void api.presencePing(editorDeviceId).catch(() => {}); };
    ping();
    const h = setInterval(ping, 10_000);
    return () => clearInterval(h);
  }, [editorDeviceId]);

  // Stale-view guard: if someone else confirmed a flash on this device AFTER our
  // session was read, our disk view is stale — warn (never block).
  const lastFlashQ = useQuery({
    queryKey: ['device-lastflash', editorDeviceId],
    queryFn: () => api.deviceLastFlash(editorDeviceId!),
    enabled: !!editorDeviceId,
    refetchInterval: 8000,
  });
  const staleFlashBy = (() => {
    const lf = lastFlashQ.data?.lastFlash;
    const created = sessionQ.data?.createdAt;
    if (!lf || !created) return null;
    if (lf.atUnix <= created) return null; // flash predates our read
    if (lf.actorUserId && lf.actorUserId === user.id) return null; // our own flash
    return lf.actorUsername ?? lf.actorUserId?.slice(0, 8) ?? '—';
  })();

  // Reload: discard unsaved edits, then ask the device to re-scan its disks and
  // open the fresh session it pushes. Clients (who can't trigger a re-scan) just
  // re-read the latest stored snapshot.
  const reload = async () => {
    setEdits({});
    setExcludedFields(new Set());
    setHistory([]);
    setManualBc(new Set());
    setSyncDate(new Set());
    setSyncBom(new Set());
    syncSeen.current.clear(); // re-apply the default-ON seeding for the fresh disks
    setSelected(new Set());
    setBulkValue('');
    setBatchMsg(null);
    setBcStartTouched(false);
    setApplyMsg(null);
    setPreviewResult(null);
    // A manual reload is a fresh start — drop any sticky flash-success banner too
    // (the post-apply auto-jump does NOT go through reload(), so its banner still
    // persists across the rescan as intended).
    setFlashDone(null);

    const deviceId = sessionQ.data?.deviceId;
    const curCreated = sessionQ.data?.createdAt ?? 0;
    if (!deviceId) {
      void qc.invalidateQueries({ queryKey: ['session', id] });
      return;
    }
    setReloading(true);
    try {
      const r = await api.requestReinventory(deviceId);
      if (!r.online) {
        message.warning(t('editorReloadOffline'));
        await qc.invalidateQueries({ queryKey: ['session', id] });
        return;
      }
      // The device re-inventories on its next heartbeat (~15 s) → new session.
      const deadline = Date.now() + 40_000;
      while (aliveRef.current && Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 1000));
        if (!aliveRef.current) return;
        const list = await api.listSessions({ deviceId, limit: 1 });
        const latest = list.items[0];
        if (latest && latest.id !== id && latest.createdAt > curCreated) {
          if (!aliveRef.current) return;
          goToSession(latest.id);
          return;
        }
      }
      if (!aliveRef.current) return;
      message.warning(t('editorReloadTimeout'));
      await qc.invalidateQueries({ queryKey: ['session', id] });
    } catch (e) {
      if (aliveRef.current) message.error((e as Error).message);
    } finally {
      if (aliveRef.current) setReloading(false);
    }
  };

  // Power-cycle the given disks (by serial), then wait for them to reboot and
  // re-inventory (same new-session mechanism reload() waits on). Admin-only.
  const runPowercycle = async (serials: string[]) => {
    const deviceId = sessionQ.data?.deviceId;
    const curCreated = sessionQ.data?.createdAt ?? 0;
    if (!deviceId || serials.length === 0) return;
    setPowercycling(serials);
    try {
      await api.powercycleDevice(deviceId, serials);
      message.info(t('pcCycleRequested', { serials: serials.join(', ') }));
      // The device reboots the disks, waits for them, then re-inventories on a
      // subsequent heartbeat → a new session. Poll up to ~150 s for it.
      const deadline = Date.now() + 150_000;
      while (aliveRef.current && Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 2000));
        if (!aliveRef.current) return;
        const list = await api.listSessions({ deviceId, limit: 1 });
        const latest = list.items[0];
        if (latest && latest.id !== id && latest.createdAt > curCreated) {
          if (!aliveRef.current) return;
          goToSession(latest.id);
          return;
        }
      }
      if (!aliveRef.current) return;
      message.warning(t('pcCycleTimeout'));
    } catch (e) {
      if (aliveRef.current) message.error((e as Error).message);
    } finally {
      if (aliveRef.current) setPowercycling(null);
    }
  };

  // Warn prominently before rebooting live disks, then power-cycle on confirm.
  const confirmPowercycle = (serials: string[]) => {
    if (serials.length === 0) return;
    modal.confirm({
      title: t('pcConfirmTitle'),
      icon: <ThunderboltOutlined style={{ color: '#faad14' }} />,
      okText: t('pcConfirmOk'),
      okButtonProps: { danger: true },
      cancelText: t('confirmApplyCancel'),
      content: (
        <div>
          <p>
            <strong>{t('pcConfirmWarning', { count: serials.length })}</strong>
          </p>
          <p>
            {t('pcConfirmSlots')}: <code>{serials.join(', ')}</code>
          </p>
        </div>
      ),
      onOk: () => runPowercycle(serials),
    });
  };

  const disks = sessionQ.data?.disks ?? [];
  const diskBySn = useMemo(() => new Map(disks.map((d) => [d.serial, d])), [disks]);

  // Serials of the currently-selected disks — every disk always has a serial, so
  // the "power-cycle selected" button works for any selection. Feeds that button.
  const selectedSerials = useMemo(() => [...selected], [selected]);

  // Keep the draggable order in sync with the disk set (preserve user order).
  const diskKey = disks.map((d) => d.serial).join('|');
  useEffect(() => {
    const serials = diskKey ? diskKey.split('|') : [];
    setOrder((prev) => {
      const kept = prev.filter((sn) => serials.includes(sn));
      const added = serials.filter((sn) => !kept.includes(sn));
      const next = [...kept, ...added];
      return next.length === prev.length && next.every((v, i) => v === prev[i]) ? prev : next;
    });
  }, [diskKey]);

  // Default-ON: sync-date and sync-BOM start enabled for every disk. A disk is
  // seeded once (tracked in `syncSeen`), so a user who unticks a disk keeps it
  // off across the periodic re-render/refetch; only genuinely-new serials get the
  // default. reload() clears `syncSeen` so a freshly-loaded session re-defaults on.
  const syncSeen = useRef<Set<string>>(new Set());
  useEffect(() => {
    const serials = diskKey ? diskKey.split('|') : [];
    const fresh = serials.filter((sn) => !syncSeen.current.has(sn));
    if (fresh.length === 0) return;
    fresh.forEach((sn) => syncSeen.current.add(sn));
    setSyncDate((prev) => { const n = new Set(prev); fresh.forEach((sn) => n.add(sn)); return n; });
    setSyncBom((prev) => { const n = new Set(prev); fresh.forEach((sn) => n.add(sn)); return n; });
  }, [diskKey]);

  // Tell the embedding workspace whether we hold unsaved edits (for the
  // switch-device confirmation).
  const isDirty = Object.keys(edits).length > 0;
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const orderedDisks = order.map((sn) => diskBySn.get(sn)).filter((d) => d !== undefined);

  // The displayed (and barcode-iteration) order: 'manual' keeps the drag order,
  // otherwise sort by the chosen field. Barcode sort uses the original value so
  // rows don't jump around while editing.
  type DiskItem = (typeof orderedDisks)[number];
  const nodeId = (d: DiskItem): number => {
    const m = /nvme(\d+)/.exec(d.devPath ?? '');
    return m ? Number(m[1]) : Number.POSITIVE_INFINITY;
  };
  const diskType = (d: DiskItem): { label: string; color: string } | null => {
    // Prefer the device/server-reported bus + form factor. Fall back to a
    // devPath guess when the backend hasn't populated `transport` yet.
    const tr = d.summary?.transport?.toLowerCase();
    const ff = d.summary?.formFactor;
    if (tr === 'nvme') return { label: 'NVMe', color: 'geekblue' };
    if (tr === 'sas') {
      // Form factor by block size: 2.5" (520) green, 3.5" (4Kn) orange.
      if (ff === '2.5') return { label: 'SAS 2.5"', color: 'green' };
      if (ff === '3.5') return { label: 'SAS 3.5"', color: 'orange' };
      return { label: 'SAS', color: 'gold' };
    }
    const p = d.devPath ?? '';
    if (/nvme/i.test(p)) return { label: 'NVMe', color: 'geekblue' };
    // Real SAS paths look like /dev/sd-72a (hyphen) or /dev/sda — match both.
    if (/(sas|\bsd)/i.test(p)) return { label: 'SAS', color: 'gold' };
    return null;
  };
  const sortKey = (d: DiskItem): number | string => {
    switch (sortField) {
      case 'slot': {
        // Sort by FRAME first, then slot within the frame: all of frame 0's
        // bays, then all of frame 64's, etc. Frame dominates the composite key
        // (× 100000 > any real slot number); unknown frame/slot sort last.
        const fr = d.frame != null && d.frame >= 0 ? d.frame : 9999;
        const sl = d.slot != null && d.slot >= 0 ? d.slot : 99999;
        return fr * 100000 + sl;
      }
      case 'id':
        return nodeId(d);
      case 'sn':
        return d.serial;
      case 'barcode':
        return d.summary?.barcode ?? '';
      default:
        return 0;
    }
  };
  const displayDisks =
    sortField === 'manual'
      ? orderedDisks
      : [...orderedDisks].sort((a, b) => {
          const ka = sortKey(a);
          const kb = sortKey(b);
          const r =
            typeof ka === 'number' && typeof kb === 'number'
              ? ka - kb
              : String(ka).localeCompare(String(kb));
          return sortDesc ? -r : r;
        });

  // Counter default: the serial of the first row's barcode (its last 6 chars).
  useEffect(() => {
    if (bcStartTouched) return;
    const first = displayDisks[0];
    const bc = first?.summary?.barcode;
    if (bc) {
      const serial = parseBarcode(bc).serial;
      if (serial && serial !== bcStart) setBcStart(serial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayDisks[0]?.serial, displayDisks[0]?.summary?.barcode, bcStartTouched]);

  /** Effective barcode for a disk: manual value, or the derived assembly. */
  const effectiveBarcode = (sn: string): string => {
    const d = diskBySn.get(sn);
    const ed = edits[sn] ?? {};
    if (manualBc.has(sn)) return ed.barcode ?? d?.summary?.barcode ?? '';
    return deriveBarcode(d?.summary ?? null, ed, syncDate.has(sn), syncBom.has(sn));
  };

  /** True when the effective (sync-derived or manual) BarCode differs from the
   * one stored on the disk — a real pending change even with no field edits. */
  const barcodeChanged = (sn: string): boolean => {
    const bc = effectiveBarcode(sn);
    return !!bc && bc !== (diskBySn.get(sn)?.summary?.barcode ?? '');
  };

  /** The disk's effective elabel BOM (edited value, else the stored one). The
   * whitelist BOM tracks this on Apply. */
  const effectiveBom = (sn: string): string => {
    const d = diskBySn.get(sn);
    return edits[sn]?.bom ?? d?.summary?.bom ?? '';
  };

  /** True when the disk carries a whitelist whose OWN stored BOM differs from the
   * disk's effective elabel BOM — a real pending change (the whitelist BOM is
   * re-synced on Apply) even when no field was edited. Without this a disk whose
   * only drift is a stale whitelist BOM never reads as "modified" and can't flash. */
  const whitelistBomChanged = (sn: string): boolean => {
    const wl = diskBySn.get(sn)?.whitelist;
    if (!wl) return false;
    const bom = effectiveBom(sn);
    return bom.length > 0 && bom !== wl.bom;
  };

  /** A disk is "modified" when it has at least one change that would ACTUALLY be
   * written — i.e. an edited/derived field that the operator hasn't opted out of
   * (unchecked in the confirm dialog or the whitelist-BOM checkbox). Mirrors the
   * three payload builders so the badge never lies. */
  const isModified = (sn: string): boolean => {
    const ed = edits[sn] ?? {};
    const d = diskBySn.get(sn);
    const sum = d?.summary;
    if (!isFieldExcluded(sn, 'bom') && ed.bom && ed.bom !== (sum?.bom ?? '')) return true;
    if (!isFieldExcluded(sn, 'level') && ed.level && ed.level !== (sum?.level ?? '')) return true;
    if (!isFieldExcluded(sn, 'barcode') && barcodeChanged(sn)) return true;
    if (!isFieldExcluded(sn, 'manufactured') && ed.manufactured && ed.manufactured !== (sum?.manufactured ?? ''))
      return true;
    if (!isFieldExcluded(sn, 'boardType') && ed.boardType && ed.boardType !== (sum?.boardType ?? '')) return true;
    if (!isFieldExcluded(sn, 'model') && ed.model && ed.model !== (sum?.model ?? '')) return true;
    if (!isFieldExcluded(sn, 'description') && ed.description && ed.description !== (sum?.description ?? ''))
      return true;
    const wl = d?.whitelist;
    if (wl) {
      if (!isFieldExcluded(sn, 'wlType') && ed.wlType !== undefined && ed.wlType !== wl.listType) return true;
      if (!isFieldExcluded(sn, 'wlVersion') && ed.wlVersion !== undefined && ed.wlVersion !== wl.listVersion)
        return true;
      if (!isFieldExcluded(sn, 'wlBom') && whitelistBomChanged(sn)) return true;
    }
    if (!isFieldExcluded(sn, 'cert') && !!authEdits[sn]?.r4) return true;
    return false;
  };

  /** Any disk with something to write (independent of the selection). Gates the
   * Preview/Apply buttons so sync-only changes are applyable. */
  const hasPendingChanges = disks.some((d) => isModified(d.serial));

  /** Display label for a disk's current certificate authority. */
  type CertStatusT = DiskItem['certStatus'];
  const certLabel = (cs: CertStatusT): string => {
    if (!cs) return t('certNone');
    if (cs.match === 'orig') return t('certOrig');
    if (cs.match === 'ca') return cs.caName;
    return t('certError');
  };

  /** Flat change rows for the confirm modal and the request payload. */
  const buildChangeRows = (): Array<{
    sn: string;
    fieldId: string;
    field: string;
    oldVal: string;
    newVal: string;
  }> => {
    const rows: Array<{ sn: string; fieldId: string; field: string; oldVal: string; newVal: string }> = [];
    for (const d of disks) {
      // Match what Apply actually flashes: only ticked disks (same filter as
      // buildChangesPerDisk), so the confirm modal's count/rows never overstate.
      if (!selected.has(d.serial)) continue;
      // No field edits is still a change when sync recomputed the BarCode.
      const ed = edits[d.serial] ?? {};
      const sum = d.summary;
      const push = (fieldId: string, field: string, oldVal: string, newVal: string | undefined): void => {
        if (newVal === undefined || newVal === oldVal) return;
        rows.push({ sn: d.serial, fieldId, field, oldVal: oldVal || '(empty)', newVal: newVal || '(empty)' });
      };
      push('bom', 'BOM', sum?.bom ?? '', ed.bom);
      push('level', t('diskLevel'), sum?.level ?? '', ed.level);
      const bcNew = effectiveBarcode(d.serial);
      if (bcNew && bcNew !== (sum?.barcode ?? '')) push('barcode', t('diskBarcode'), sum?.barcode ?? '', bcNew);
      push('manufactured', t('diskManufactured'), sum?.manufactured ?? '', ed.manufactured);
      push('boardType', t('diskBoardType'), sum?.boardType ?? '', ed.boardType);
      push('model', t('diskModel'), sum?.model ?? '', ed.model);
      push('description', t('diskDescription'), sum?.description ?? '', ed.description);
      if (d.whitelist) {
        push('wlType', t('wlType'), d.whitelist.listType, ed.wlType);
        push('wlVersion', t('wlVersion'), d.whitelist.listVersion, ed.wlVersion);
        // Stale whitelist BOM re-synced to the elabel BOM (a flashable change on
        // its own, with no field edit).
        const effBom = effectiveBom(d.serial);
        if (effBom.length > 0 && effBom !== d.whitelist.bom)
          push('wlBom', t('wlBomRef'), d.whitelist.bom, effBom);
      }
      if (authEdits[d.serial]?.r4) {
        // Current = the cert actually on the disk now; New = what we'll write.
        const curLabel = certLabel(d.certStatus);
        const st = certStaged[d.serial];
        let newLabel: string;
        if (st?.dir === 'ca') {
          newLabel = t('certNewCa', { name: st.caName });
        } else if (st?.dir === 'orig') {
          newLabel = t('certNewOrig', {
            source: t(`backupSrc_${st.source}` as 'backupSrc_pre_apply'),
            when: fmtDateTimeMin(st.at),
          });
        } else {
          newLabel = t('certImported');
        }
        rows.push({
          sn: d.serial,
          fieldId: 'cert',
          field: t('certRegion'),
          oldVal: curLabel,
          newVal: newLabel,
        });
      }
    }
    return rows;
  };

  /** Spreadsheet dump of the disk list in the order shown on screen. Two rows
   * per disk — the stored values on top, the ones we'd write below — with the
   * identity columns (slot/frame/SN/type/changed) merged across the pair.
   * Unedited disks are included too (marked "not changed") so the file doubles
   * as an inventory of what is in the box. It covers the whole list, not just
   * the ticked rows: this exports what you SEE, not what Apply would flash. */
  const exportXls = (): void => {
    const sheet: XlsxSheet = {
      name: t('xlsSheetChanges'),
      single: [
        { header: t('xlsColSlot'), width: 7 },
        { header: t('xlsColFrame'), width: 7 },
        { header: t('historyDiskSn'), width: 22 },
        { header: t('historyDiskType'), width: 11 },
        { header: t('xlsColChanged'), width: 10 },
      ],
      // Every field this editor can write, each in its own column.
      pair: [
        { header: 'BOM', width: 14 },
        { header: 'Level', width: 9 },
        { header: 'BarCode', width: 24 },
        { header: 'Manufactured', width: 14 },
        { header: 'BoardType', width: 18 },
        { header: 'Model', width: 22 },
        { header: 'Description', width: 46 },
      ],
      wasLabel: t('xlsWasRow'),
      nowLabel: t('xlsNowRow'),
    };
    const records: XlsxRecord[] = displayDisks.map((d) => {
      const sn = d.serial;
      const sum = d.summary;
      const ed = edits[sn] ?? {};
      const bcOld = sum?.barcode ?? '';
      const bcNew = effectiveBarcode(sn);
      const was = [
        sum?.bom ?? '',
        sum?.level ?? '',
        bcOld,
        sum?.manufactured ?? '',
        sum?.boardType ?? '',
        sum?.model ?? '',
        sum?.description ?? '',
      ];
      // The "new" row carries the resulting value, not just the diff, so the
      // bottom line reads as the disk's complete post-flash label. The writer
      // highlights the cells that actually differ.
      const now = [
        ed.bom ?? was[0],
        ed.level ?? was[1],
        bcNew || bcOld,
        ed.manufactured ?? was[3],
        ed.boardType ?? was[4],
        ed.model ?? was[5],
        ed.description ?? was[6],
      ];
      return {
        single: [
          d.slot ?? null,
          d.frame ?? null,
          sn,
          diskType(d)?.label ?? '',
          isModified(sn) ? t('xlsYes') : t('xlsNo'),
        ],
        was,
        now,
      };
    });
    const devId = sessionQ.data?.deviceId;
    const dev = devicesQ.data?.find((x) => x.id === devId)?.label || devId?.substring(0, 8) || 'disks';
    downloadXlsx(`disks_${dev}_${stampNow()}`, sheet, records);
  };

  const buildChangesPerDisk = (): Record<string, Record<string, unknown>> => {
    const changesPerDisk: Record<string, Record<string, unknown>> = {};
    // Walk every disk, not just edited ones: with sync on by default a disk can
    // have a recomputed BarCode and no field edits, and that still has to be
    // written (it is what the row shows and what "modified" now reports).
    for (const d of disks) {
      const sn = d.serial;
      // Only flash disks whose checkbox is ticked — unchecking excludes a disk.
      if (!selected.has(sn)) continue;
      const e = edits[sn] ?? {};
      const sum = diskBySn.get(sn)?.summary;
      const c: Record<string, unknown> = {};
      if (e.bom && !isFieldExcluded(sn, 'bom')) c.bom = e.bom;
      if (e.level && !isFieldExcluded(sn, 'level')) c.level = e.level;
      const elabelFields: Record<string, string> = {};
      const bcNew = effectiveBarcode(sn);
      if (bcNew && bcNew !== (sum?.barcode ?? '') && !isFieldExcluded(sn, 'barcode'))
        elabelFields.BarCode = bcNew;
      if (e.model && !isFieldExcluded(sn, 'model')) elabelFields.Model = e.model;
      if (e.description && !isFieldExcluded(sn, 'description')) elabelFields.Description = e.description;
      if (e.manufactured && !isFieldExcluded(sn, 'manufactured')) elabelFields.Manufactured = e.manufactured;
      if (e.boardType && !isFieldExcluded(sn, 'boardType')) elabelFields.BoardType = e.boardType;
      if (Object.keys(elabelFields).length > 0) c.elabelFields = elabelFields;
      if (Object.keys(c).length > 0) changesPerDisk[sn] = c;
    }
    return changesPerDisk;
  };

  // Per-disk whitelist LIST edits (type/version). The whitelist BOM is not sent
  // here — the server derives it from changesPerDisk[sn].bom so it always tracks
  // the BOM field. A disk with no current whitelist is never given one.
  const buildWhitelistPerDisk = (): Record<
    string,
    { listType?: string; listVersion?: string; bom?: string }
  > => {
    const out: Record<string, { listType?: string; listVersion?: string; bom?: string }> = {};
    for (const d of disks) {
      const sn = d.serial;
      if (!selected.has(sn) || !d.whitelist) continue;
      const e = edits[sn] ?? {};
      const entry: { listType?: string; listVersion?: string; bom?: string } = {};
      if (e.wlType !== undefined && e.wlType !== d.whitelist.listType && !isFieldExcluded(sn, 'wlType'))
        entry.listType = e.wlType;
      if (e.wlVersion !== undefined && e.wlVersion !== d.whitelist.listVersion && !isFieldExcluded(sn, 'wlVersion'))
        entry.listVersion = e.wlVersion;
      // Re-sync a stale whitelist BOM to the disk's effective elabel BOM even when
      // the BOM field itself wasn't edited — the server otherwise only tracks the
      // whitelist BOM off an actual elabel edit (changesPerDisk[sn].bom).
      const effBom = effectiveBom(sn);
      if (effBom.length > 0 && effBom !== d.whitelist.bom && !isFieldExcluded(sn, 'wlBom'))
        entry.bom = effBom;
      if (Object.keys(entry).length > 0) out[sn] = entry;
    }
    return out;
  };

  // Raw DISK_AUTHENTICATION (r4/r5) staged from a .hwrsv import, for the ticked
  // disks. Written verbatim on Apply (no field diff — the region is opaque).
  const buildAuthRegionsPerDisk = (): Record<string, { r4: string; r5: string; caId?: string }> => {
    const out: Record<string, { r4: string; r5: string; caId?: string }> = {};
    for (const sn of Object.keys(authEdits)) {
      if (!selected.has(sn) || isFieldExcluded(sn, 'cert')) continue;
      const e = authEdits[sn]!;
      if (!e.r4) continue;
      // A conversion to a CA carries its caId (for the usage tally); a restore or
      // a raw .hwrsv import does not.
      const st = certStaged[sn];
      out[sn] = { r4: e.r4, r5: e.r5, caId: st?.dir === 'ca' ? st.caId : undefined };
    }
    return out;
  };

  const preview = useMutation({
    mutationFn: () =>
      api.previewPatch(
        id!,
        buildChangesPerDisk() as Parameters<typeof api.previewPatch>[1],
      ),
    onSuccess: (r) => setPreviewResult(r),
    onError: (err: Error) => setApplyMsg({ ok: false, text: `${t('previewFailed')}: ${err.message}` }),
  });

  // True only after an apply initiated from THIS editor — gates the auto-jump
  // to the fresh post-apply session so that merely *opening* an old confirmed
  // session never navigates away.
  const appliedHereRef = useRef(false);
  // Whether the last apply asked the device to power-cycle the disks afterwards.
  // A power-cycle reboots the disks (up to ~150 s) before the device re-inventories,
  // so the post-apply auto-jump below must wait far longer than a plain flash.
  const appliedPowerCycleRef = useRef(false);
  // Drag-reorder source SN. Declared here (with the other hooks, before the
  // early returns below) so the hook order stays stable across renders.
  const dragSn = useRef<string | null>(null);
  // Live drag visuals: which row is being dragged, and which row it's hovering
  // over (the drop target). State (not a ref) so rowClassName re-renders.
  const [draggingSn, setDraggingSn] = useState<string | null>(null);
  const [dragOverSn, setDragOverSn] = useState<string | null>(null);
  const apply = useMutation({
    mutationFn: (powerCycleAfter: boolean) => {
      // A new flash supersedes any sticky success banner from a previous one.
      setFlashDone(null);
      // Remember it so the post-apply auto-jump waits long enough for the reboot.
      appliedPowerCycleRef.current = powerCycleAfter;
      return api.applyChanges(
        id!,
        buildChangesPerDisk() as Parameters<typeof api.applyChanges>[1],
        powerCycleAfter,
        buildWhitelistPerDisk(),
        buildAuthRegionsPerDisk(),
      );
    },
    onSuccess: () => {
      // The session is now response_built; the status-driven banner takes over
      // (waiting for the device → per-disk result).
      appliedHereRef.current = true;
      setApplyMsg(null);
      setEdits({});
      setAuthEdits({});
      setCertStaged({});
      setExcludedFields(new Set());
      setHistory([]);
      setConfirmOpen(false);
      setPreviewResult(null);
      qc.invalidateQueries({ queryKey: ['session', id] });
      qc.invalidateQueries({ queryKey: ['my-quota'] });
    },
    onError: (err: Error) => {
      setApplyMsg({ ok: false, text: `${t('applyFailed')}: ${err.message}` });
      setConfirmOpen(false);
    },
  });

  const hasEdits = useMemo(() => Object.keys(edits).length > 0, [edits]);

  // Convert a disk's r4/r5 certificate: 'our' re-issues the factory leaf onto our
  // OEM CA; 'orig' restores the original from a backup. Both STAGE the new region
  // bytes into authEdits — the disk then reads as modified and flashes through the
  // normal Apply path (backup + confirm + quota).
  const certConvert = useMutation({
    mutationFn: async (v: { sn: string; to: 'ca' | 'orig'; caId?: string }) => {
      if (v.to === 'ca') {
        const r = await api.cloneDiskCert(id!, v.sn, v.caId);
        return { sn: v.sn, r4: r.r4, r5: r.r5, staged: { dir: 'ca' as const, caId: r.caId, caName: r.caName } };
      }
      const r = await api.restoreDiskCert(id!, v.sn);
      return {
        sn: v.sn,
        r4: r.r4,
        r5: r.r5,
        staged: { dir: 'orig' as const, source: r.backupSource, at: r.backupAt },
      };
    },
    onSuccess: (res) => {
      setAuthEdits((prev) => ({ ...prev, [res.sn]: { r4: res.r4, r5: res.r5 } }));
      setCertStaged((prev) => ({ ...prev, [res.sn]: res.staged }));
      message.success(t(res.staged.dir === 'ca' ? 'certStagedOur' : 'certStagedOrig'));
    },
    onError: (e: Error) => message.error(`${t('certConvertFailed')}: ${e.message}`),
  });

  // Bulk cert change over the ticked disks: convert each eligible disk to a CA
  // (dir 'ca') or restore its original (dir 'orig'). Ineligible disks (no cert,
  // CN≠SN, or already original for a restore) are skipped. Runs the per-disk
  // endpoint sequentially, then stages all results at once.
  const certConvertBulk = useMutation({
    mutationFn: async (v: { to: 'ca' | 'orig'; caId?: string }) => {
      const auth: Record<string, { r4: string; r5: string }> = {};
      const staged: Record<string, StagedCert> = {};
      let skipped = 0;
      const failed: string[] = [];
      for (const sn of selectedSerials) {
        const cs = diskBySn.get(sn)?.certStatus;
        const eligible =
          v.to === 'ca'
            ? !!cs && (cs.match === 'orig' || cs.match === 'ca')
            : !!cs && cs.match !== 'orig';
        if (!eligible) {
          skipped++;
          continue;
        }
        try {
          if (v.to === 'ca') {
            const r = await api.cloneDiskCert(id!, sn, v.caId);
            auth[sn] = { r4: r.r4, r5: r.r5 };
            staged[sn] = { dir: 'ca', caId: r.caId, caName: r.caName };
          } else {
            const r = await api.restoreDiskCert(id!, sn);
            auth[sn] = { r4: r.r4, r5: r.r5 };
            staged[sn] = { dir: 'orig', source: r.backupSource, at: r.backupAt };
          }
        } catch {
          failed.push(sn);
        }
      }
      return { auth, staged, ok: Object.keys(auth).length, skipped, failed };
    },
    onSuccess: (res) => {
      setAuthEdits((prev) => ({ ...prev, ...res.auth }));
      setCertStaged((prev) => ({ ...prev, ...res.staged }));
      if (res.ok > 0) message.success(t('certBulkStaged', { n: res.ok }));
      if (res.failed.length > 0) message.warning(t('certBulkFailed', { n: res.failed.length }));
      if (res.ok === 0 && res.failed.length === 0) message.info(t('certBulkNone', { n: res.skipped }));
    },
    onError: (e: Error) => message.error(`${t('certConvertFailed')}: ${e.message}`),
  });

  // Drop a staged certificate conversion for a disk (before it's flashed).
  const clearStagedCert = (sn: string): void => {
    setAuthEdits((prev) => {
      const n = { ...prev };
      delete n[sn];
      return n;
    });
    setCertStaged((prev) => {
      const n = { ...prev };
      delete n[sn];
      return n;
    });
  };

  // After a successful flash the device re-inventories the disks, producing a
  // fresh session with the just-written values. Jump to it so the disk rows
  // show the new state (and become editable again).
  const refreshedRef = useRef(false);
  useEffect(() => {
    const sd = sessionQ.data;
    if (!sd || sd.status !== 'confirmed') {
      refreshedRef.current = false;
      return;
    }
    // Only auto-jump when the flash was initiated from this editor — never when
    // an already-confirmed session is opened directly.
    const ok =
      appliedHereRef.current &&
      !!sd.confirmResults && sd.confirmResults.length > 0 && sd.confirmResults.every((r) => r.status === 'OK');
    if (!ok || refreshedRef.current) return;
    refreshedRef.current = true;
    appliedHereRef.current = false;
    // A power-cycle reboots the disks before the device can re-inventory, which
    // takes far longer than a plain flash (matches runPowercycle's ~150 s wait).
    // Without this the poll gave up after 30 s and the list looked stuck until a
    // manual reload — which itself raced the still-rebooting disk.
    const powerCycled = appliedPowerCycleRef.current;
    appliedPowerCycleRef.current = false;
    // Snapshot the success into the sticky banner BEFORE we navigate away — it
    // must outlive the jump to the fresh re-inventory session below.
    setFlashDone({ results: sd.confirmResults!, diskCount: sd.diskCount, sessionId: sd.id });
    const deviceId = sd.deviceId;
    const baseCreated = sd.createdAt;
    let cancelled = false;
    const deadline = Date.now() + (powerCycled ? 165_000 : 30_000);
    setPostApplyWaiting(true);
    // The device's post-apply re-inventory only refreshed THIS session (now
    // confirmed + locked); no NEW session is created on its own, so the poll below
    // would wait forever. Ask the device to re-inventory now that the flash and
    // power-cycle have settled — it produces a fresh EDITABLE session on its next
    // heartbeat, which the poll then jumps to. Admin-only (clients can't rescan;
    // they keep the refreshed confirmed view). Best-effort — ignore failures.
    if (deviceId && isAdminRole(user.role)) {
      void api.requestReinventory(deviceId).catch(() => {});
    }
    void (async () => {
      try {
        while (!cancelled && Date.now() < deadline) {
          try {
            const list = await api.listSessions({ deviceId, limit: 1 });
            const latest = list.items[0];
            if (latest && latest.id !== id && latest.createdAt >= baseCreated) {
              message.success(t('editorDisksRefreshed'));
              goToSession(latest.id);
              return;
            }
          } catch {
            /* keep polling until the deadline */
          }
          await new Promise((r) => setTimeout(r, 2000));
        }
      } finally {
        if (!cancelled) setPostApplyWaiting(false);
      }
    })();
    return () => {
      cancelled = true;
      setPostApplyWaiting(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionQ.data?.status, sessionQ.data?.confirmResults, id]);

  if (sessionQ.isLoading) return <div className="card">{t('loadingEllipsis')}</div>;
  if (!sessionQ.data) return <div className="card muted">404</div>;
  const s = sessionQ.data;

  // Editing is only allowed before an apply. Once applied, the list is locked
  // while the device writes, then shows the device's per-disk result.
  const editable = s.status === 'inventory_received' || s.status === 'editing';
  const applying = s.status === 'response_built' || s.status === 'response_delivered';
  const confirmResults = s.confirmResults ?? null;

  // Quota costs one unit per disk flashed. Count the disks that will flash
  // (ticked + edited) and compare to the client's remaining balance.
  // A disk flashes if it has region edits OR a whitelist edit (either counts once).
  const flashCount = new Set([
    ...Object.keys(buildChangesPerDisk()),
    ...Object.keys(buildWhitelistPerDisk()),
    ...Object.keys(buildAuthRegionsPerDisk()),
  ]).size;
  const quotaAvailable = user.role === 'client' ? (quotaQ.data?.available ?? 0) : Infinity;
  const quotaShort = user.role === 'client' && flashCount > quotaAvailable;

  // The disk's stored value for an editable field (barcodeSerial → the serial
  // embedded in the disk's own BarCode). Typing a cell back to this un-modifies.
  const originalFieldValue = (sn: string, field: EditableField): string => {
    const sum = diskBySn.get(sn)?.summary;
    if (field === 'barcode') return sum?.barcode ?? '';
    if (field === 'barcodeSerial') return sum?.barcode ? (parseBarcode(sum.barcode).serial ?? '') : '';
    if (field === 'wlType') return diskBySn.get(sn)?.whitelist?.listType ?? '';
    if (field === 'wlVersion') return diskBySn.get(sn)?.whitelist?.listVersion ?? '';
    return (sum?.[field as PlainField] ?? '') as string;
  };

  const setEdit = (
    sn: string,
    field: EditableField,
    value: string,
    // Passed by the manual/sync toggles so their set-membership flip rides along
    // in the same undo entry as the barcode edit they make.
    extra?: { manualPrev?: boolean; syncPrev?: boolean },
  ) => {
    const prev = edits[sn]?.[field];
    // Empty, or equal to the disk's stored value → drop the edit (un-modify).
    const orig = originalFieldValue(sn, field);
    const effectiveNew = value.length === 0 || value === orig ? undefined : value;
    if (effectiveNew !== prev || extra !== undefined) {
      setHistory((h) => [
        ...h,
        { serial: sn, field, prev, manualPrev: extra?.manualPrev, syncPrev: extra?.syncPrev },
      ]);
    }
    setEdits((prevEdits) => {
      const next = { ...prevEdits };
      const cur = { ...(next[sn] ?? {}) };
      if (effectiveNew === undefined) delete cur[field];
      else cur[field] = effectiveNew;
      if (Object.keys(cur).length === 0) delete next[sn];
      else next[sn] = cur;
      return next;
    });
    // Editing a disk includes it in the flash set (its checkbox); the operator
    // can uncheck it to leave it out (e.g. to fit a limited quota).
    if (effectiveNew !== undefined) {
      setSelected((prev) => (prev.has(sn) ? prev : new Set(prev).add(sn)));
    }
  };

  const toggleSelect = (sn: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sn)) next.delete(sn);
      else next.add(sn);
      return next;
    });
  };

  const toggleManual = (sn: string) => {
    const wasManual = manualBc.has(sn);
    // The barcode edit + the manual-flag flip share one undo entry (extra).
    if (wasManual) {
      setEdit(sn, 'barcode', '', { manualPrev: true });
    } else {
      // Start manual editing from whatever is currently shown.
      const current = effectiveBarcode(sn);
      setEdit(sn, 'barcode', current || '', { manualPrev: false });
    }
    setManualBc((prev) => {
      const next = new Set(prev);
      if (wasManual) next.delete(sn);
      else next.add(sn);
      return next;
    });
  };

  const toggleSyncDate = (sn: string) => {
    const syncPrev = syncDate.has(sn);
    setHistory((h) => [...h, { serial: sn, syncPrev }]);
    setSyncDate((prev) => {
      const next = new Set(prev);
      if (syncPrev) next.delete(sn);
      else next.add(sn);
      return next;
    });
  };

  const toggleSyncBom = (sn: string) => {
    const bomSyncPrev = syncBom.has(sn);
    setHistory((h) => [...h, { serial: sn, bomSyncPrev }]);
    setSyncBom((prev) => {
      const next = new Set(prev);
      if (bomSyncPrev) next.delete(sn);
      else next.add(sn);
      return next;
    });
  };

  // --- .hwrsv export / import (admin only) -----------------------------------
  // Load .hwrsv-extracted elabel fields as PENDING edits: for each field that
  // differs from the disk's current summary, stage a setEdit (which also selects
  // the disk). The barcode needs manual mode to take effect. Returns the number
  // of fields staged (0 → nothing differed).
  const applyImportedFields = (serial: string, fields: HwrsvFields): number => {
    const sum = diskBySn.get(serial)?.summary ?? null;
    let n = 0;
    const plain: Array<
      [PlainField, 'bom' | 'level' | 'model' | 'description' | 'manufactured' | 'boardType']
    > = [
      ['bom', 'bom'],
      ['level', 'level'],
      ['model', 'model'],
      ['description', 'description'],
      ['manufactured', 'manufactured'],
      ['boardType', 'boardType'],
    ];
    for (const [field, key] of plain) {
      const v = fields[key];
      if (v == null) continue;
      const cur = (sum?.[field] ?? '') as string;
      if (v !== cur) {
        setEdit(serial, field, v);
        n++;
      }
    }
    const bc = fields.barcode;
    if (bc != null && bc !== (sum?.barcode ?? '')) {
      const manualPrev = manualBc.has(serial);
      setManualBc((prev) => (prev.has(serial) ? prev : new Set(prev).add(serial)));
      setEdit(serial, 'barcode', bc, { manualPrev });
      n++;
    }
    // Whitelist list type/version from a v2 .hwrsv → pending edits (only for a
    // disk that currently has a whitelist; setEdit drops a no-op change anyway).
    const wl = fields.whitelist;
    const curWl = diskBySn.get(serial)?.whitelist ?? null;
    if (wl && curWl) {
      if (wl.listType !== curWl.listType) {
        setEdit(serial, 'wlType', wl.listType);
        n++;
      }
      if (wl.listVersion !== curWl.listVersion) {
        setEdit(serial, 'wlVersion', wl.listVersion);
        n++;
      }
    }
    // Raw DISK_AUTHENTICATION (r4/r5) — a changed cert region from the .hwrsv.
    // Stage it as a flashable auth edit (no editable field) and select the disk.
    if (fields.authRegions && fields.authRegions.r4) {
      setAuthEdits((prev) => ({ ...prev, [serial]: fields.authRegions! }));
      setSelected((prev) => (prev.has(serial) ? prev : new Set(prev).add(serial)));
      n++;
    }
    return n;
  };

  const exportDisk = (serial: string): void => {
    void api.exportDiskHwrsv(s.id, serial).catch((e: Error) =>
      message.error(`${t('hwrsvExportFailed')}: ${e.message}`),
    );
  };

  const exportSelected = (): void => {
    void api.exportSelectedHwrsv(s.id, [...selected]).catch((e: Error) =>
      message.error(`${t('hwrsvExportFailed')}: ${e.message}`),
    );
  };

  const importDisk = async (serial: string): Promise<void> => {
    const file = await pickHwrsvFile();
    if (!file) return;
    try {
      const r = await api.importDiskHwrsv(s.id, serial, await file.arrayBuffer());
      const n = applyImportedFields(r.serial ?? serial, r.fields ?? {});
      if (n > 0) message.success(t('hwrsvImportedFields', { n }));
      else message.info(t('hwrsvImportedNone'));
    } catch (e) {
      message.error(`${t('hwrsvImportFailed')}: ${(e as Error).message}`);
    }
  };

  const importCombined = async (): Promise<void> => {
    const file = await pickHwrsvFile();
    if (!file) return;
    try {
      const r = await api.importHwrsv(s.id, await file.arrayBuffer());
      let applied = 0;
      for (const item of r.disks ?? []) {
        if (applyImportedFields(item.serial, item.fields ?? {}) > 0) applied++;
      }
      message.success(t('hwrsvImportedPending', { n: applied }));
    } catch (e) {
      message.error(`${t('hwrsvImportFailed')}: ${(e as Error).message}`);
    }
  };

  const copySerial = (sn: string): void => {
    void navigator.clipboard
      .writeText(sn)
      .then(() => message.success(t('copied')))
      .catch(() => message.error(t('copyFailed')));
  };

  // Bulk fill: set one field across every selected disk (HwReserveEditor group op).
  const bulkFill = (field: PlainField, value: string) => {
    if (selected.size === 0) {
      setBatchMsg(t('batchNothingSelected'));
      return;
    }
    if (!value) return;
    for (const d of displayDisks) {
      if (!selected.has(d.serial)) continue;
      const current = (d.summary?.[field] ?? '') as string;
      if (value !== current) setEdit(d.serial, field, value);
    }
  };

  // BarCode counter: the serial inside the barcode walks +1 per selected disk in
  // display order, starting from bcStart. Manual-mode and barcode-less disks are
  // skipped with a note.
  const runBarcodeCounter = () => {
    if (selected.size === 0) {
      setBatchMsg(t('batchNothingSelected'));
      return;
    }
    if (!/\d/.test(bcStart)) {
      setBatchMsg(null);
      return;
    }
    const notes: string[] = [];
    let iterated = 0;
    for (const d of displayDisks) {
      if (!selected.has(d.serial)) continue;
      if (manualBc.has(d.serial)) {
        notes.push(t('batchBarcodeManualSkip', { sn: d.serial }));
      } else if (!d.summary?.barcode) {
        notes.push(t('batchBarcodeNoBarcode', { sn: d.serial }));
      } else {
        const serial = incrementSerial(bcStart.padStart(6, '0').slice(-6), iterated);
        if (serial === null) {
          notes.push(t('batchBarcodeOverflow', { sn: d.serial }));
        } else {
          iterated++;
          const ownSerial = parseBarcode(d.summary.barcode).serial;
          setEdit(d.serial, 'barcodeSerial', serial === ownSerial ? '' : serial);
        }
      }
    }
    setBatchMsg(notes.length > 0 ? notes.join('; ') : null);
  };

  // Manual reorder (replaces card drag & drop): swap a row up/down in `order`.
  const moveRow = (sn: string, dir: -1 | 1) => {
    setOrder((prev) => {
      const arr = [...prev];
      const i = arr.indexOf(sn);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= arr.length) return prev;
      [arr[i], arr[j]] = [arr[j]!, arr[i]!];
      return arr;
    });
  };

  // HTML5 drag-to-reorder (manual sort only). The grip icon is the drag source;
  // the whole row is the drop target. Moving `fromSn` to `toSn`'s slot mutates
  // the same `order` state the up/down arrows and the BarCode counter iterate.
  // (dragSn ref is declared above with the other hooks, before the early returns.)
  const reorderTo = (fromSn: string, toSn: string) => {
    if (fromSn === toSn) return;
    setOrder((prev) => {
      const arr = [...prev];
      const from = arr.indexOf(fromSn);
      const to = arr.indexOf(toSn);
      if (from < 0 || to < 0) return prev;
      arr.splice(from, 1);
      arr.splice(to, 0, fromSn);
      return arr;
    });
  };

  // Row tint by state (mirrors HwReserveEditor): applied/error > preview > modified.
  const previewBySn = new Map((previewResult?.disks ?? []).map((d) => [d.serial, d]));
  const confirmBySn = new Map((confirmResults ?? []).map((r) => [r.serial, r]));
  const rowBaseClass = (d: DiskItem): string => {
    const cr = confirmBySn.get(d.serial);
    if (cr) return cr.status === 'OK' ? 'row-applied' : 'row-error';
    const pv = previewBySn.get(d.serial);
    if (pv) {
      if (pv.outcome.rejected.length > 0) return 'row-error';
      if (pv.totalBytesChanged > 0) return 'row-applicable';
      return 'row-skipped';
    }
    return isModified(d.serial) ? 'row-modified' : '';
  };
  const rowClassName = (d: DiskItem): string => {
    const base = rowBaseClass(d);
    if (draggingSn === d.serial) return `${base} row-dragging`.trim();
    if (dragOverSn === d.serial && draggingSn && draggingSn !== d.serial)
      return `${base} row-drop-target`.trim();
    return base;
  };

  // --- table columns ---------------------------------------------------------
  // Left-pinned identity block: selection + order + frame + slot + serial stay
  // put while the middle field columns scroll horizontally on narrow screens.
  // (Antd needs a *contiguous* fixed-left run, so order/frame/slot are pinned
  // too — they sit between the selection column and Serial.)
  // A column header with a bold main line and a muted second line naming the
  // extra sub-fields packed into that column (Frame/Slot/Node, Valid/Cert, …).
  const colHeader = (main: string, sub?: string): React.ReactNode => (
    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
      <span>{main}</span>
      {sub && <span style={{ fontSize: 10, opacity: 0.55, marginTop: 2 }}>{sub}</span>}
    </div>
  );

  const orderCol: ColumnsType<DiskItem>[number] = {
    title: colHeader(t('colOrder'), `${t('colType')} · ${t('colFrame')} · ${t('diskSlot')} · ${t('diskNode')}`),
    key: 'order',
    fixed: 'left',
    width: 112,
    render: (_: unknown, d: DiskItem, idx: number) => {
      const ty = diskType(d);
      return (
        <div>
          <Space size={2}>
            <span className="order-badge">{idx + 1}</span>
            {ty && (
              <Tooltip title={t('diskTypeHint', { type: ty.label })} mouseEnterDelay={1}>
                <span className="type-badge">
                  <span className="type-dot" style={{ background: TYPE_DOT[ty.color] ?? '#8a99a8' }} />
                  {ty.label}
                </span>
              </Tooltip>
            )}
            {sortField === 'manual' && (
              <>
                <Tooltip title={t('dragHandle')}>
                <span
                  className="drag-grip"
                  draggable
                  onDragStart={(e) => {
                    dragSn.current = d.serial;
                    setDraggingSn(d.serial);
                    e.dataTransfer.effectAllowed = 'move';
                    // Native drag ghost: a snapshot of the row's (fixed-left)
                    // <tr> follows the cursor, so the row visibly "lifts".
                    const tr = (e.currentTarget as HTMLElement).closest('tr');
                    if (tr) {
                      const r = tr.getBoundingClientRect();
                      e.dataTransfer.setDragImage(tr, e.clientX - r.left, e.clientY - r.top);
                    }
                  }}
                  onDragEnd={() => {
                    dragSn.current = null;
                    setDraggingSn(null);
                    setDragOverSn(null);
                  }}
                >
                  <HolderOutlined />
                </span>
                </Tooltip>
                <Tooltip title={t('moveUp')}>
                <Button
                  type="text"
                  size="small"
                  icon={<ArrowUpOutlined />}
                  disabled={idx === 0}
                  onClick={() => moveRow(d.serial, -1)}
                />
                </Tooltip>
                <Tooltip title={t('moveDown')}>
                <Button
                  type="text"
                  size="small"
                  icon={<ArrowDownOutlined />}
                  disabled={idx === displayDisks.length - 1}
                  onClick={() => moveRow(d.serial, 1)}
                />
                </Tooltip>
              </>
            )}
          </Space>
          <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
            {d.frame != null && (
              <Tooltip title={t('diskFrameHint')}>
                <div>
                  {t('colFrame')}: <strong>{d.frame}</strong>
                </div>
              </Tooltip>
            )}
            {d.slot != null && (
              <Tooltip title={t('diskSlotHint')}>
                <div>
                  {t('diskSlot')}: <strong>{d.slot}</strong>
                </div>
              </Tooltip>
            )}
            {Number.isFinite(nodeId(d)) && (
              <Tooltip title={t('diskNodeIdHint')}>
                <div>
                  {t('diskNode')}: <strong>{nodeId(d)}</strong>
                </div>
              </Tooltip>
            )}
          </div>
          {d.devPath && (
            <div>
              <code style={{ fontSize: 11 }}>{d.devPath}</code>
            </div>
          )}
        </div>
      );
    },
  };

  const serialCol: ColumnsType<DiskItem>[number] = {
    title: colHeader(t('sortSn'), `${t('hdrValid')} · ${t('hdrCert')} · ${t('hdrStatus')}`),
    key: 'serial',
    fixed: 'left',
    width: 150,
    render: (_: unknown, d: DiskItem) => {
      const integ = d.summary?.integrity;
      return (
        <div>
          <Space size={2}>
            <code style={{ fontSize: 12 }}>{d.serial}</code>
            <Tooltip title={t('copySerial')}>
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copySerial(d.serial)}
              />
            </Tooltip>
          </Space>
          {d.sddebugValid && (
            <div style={{ marginTop: 4 }}>
              <Tooltip title={t('diskValidHint')}>
                <span
                  className={d.sddebugValid === 'Normal' ? 'status ok' : 'status error'}
                >
                  {d.sddebugValid}
                </span>
              </Tooltip>
            </div>
          )}
          {/* Certificate status badge + the conversion control on ONE row (the
              control sits to the RIGHT of Orig/Our). The control is a staged
              "pending" pill (amber, with a clear ×) when a change is staged,
              otherwise the Orig→Our / Our→Orig action pill for admins. */}
          {(d.certStatus || authEdits[d.serial]?.r4) && (
            <div
              className="row"
              style={{ gap: 6, marginTop: 4, alignItems: 'center', flexWrap: 'wrap' }}
            >
              {d.certStatus && (
                <Tooltip title={t('certStatusHint')}>
                  <span
                    className={
                      d.certStatus.match === 'orig'
                        ? 'status ok'
                        : d.certStatus.match === 'ca'
                          ? 'status info'
                          : 'status error'
                    }
                  >
                    {certLabel(d.certStatus)}
                  </span>
                </Tooltip>
              )}
              {authEdits[d.serial]?.r4 ? (
                <>
                  {/* Amber "pending" pill — a staged change, distinct from the
                      current-cert status colour. */}
                  <Tooltip title={certStaged[d.serial] ? t('certStagedHint') : t('certImportedHint')}>
                    <span className="status warn">
                      <ClockCircleOutlined style={{ marginRight: 4 }} />
                      {(() => {
                        const st = certStaged[d.serial];
                        if (st?.dir === 'ca') return `→ ${st.caName}`;
                        if (st?.dir === 'orig') return t('certToOrig');
                        return t('certImportedBadge');
                      })()}
                    </span>
                  </Tooltip>
                  {editable && (
                    <Tooltip title={t('certClearStaged')}>
                      <Button
                        type="text"
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => clearStagedCert(d.serial)}
                      />
                    </Tooltip>
                  )}
                </>
              ) : (
                isAdminRole(user.role) &&
                editable &&
                d.certStatus &&
                (d.certStatus.match === 'orig' || d.certStatus.match === 'ca') &&
                (() => {
                  const cur = d.certStatus;
                  const curCaId = cur.match === 'ca' ? cur.caId : undefined;
                  const targets = signableCas.filter((c) => c.id !== curCaId);
                  const items = [
                    ...targets.map((c) => ({ key: `ca:${c.id}`, label: t('certConvertTo', { name: c.name }) })),
                    // Restore original only makes sense when not already original.
                    ...(cur.match !== 'orig'
                      ? [{ key: 'orig', label: t('certRestoreOriginal') }]
                      : []),
                  ];
                  if (items.length === 0) return null;
                  const busy = certConvert.isPending && certConvert.variables?.sn === d.serial;
                  return (
                    <Dropdown
                      trigger={['click']}
                      menu={{
                        items,
                        onClick: ({ key }) => {
                          if (key === 'orig') {
                            modal.confirm({
                              title: t('certToOrigConfirm'),
                              okText: t('certConvertOk'),
                              cancelText: t('cancel'),
                              onOk: () => certConvert.mutate({ sn: d.serial, to: 'orig' }),
                            });
                          } else {
                            const caId = key.slice(3);
                            const name = targets.find((c) => c.id === caId)?.name ?? '';
                            modal.confirm({
                              title: t('certToCaConfirm', { name }),
                              okText: t('certConvertOk'),
                              cancelText: t('cancel'),
                              onOk: () => certConvert.mutate({ sn: d.serial, to: 'ca', caId }),
                            });
                          }
                        },
                      }}
                    >
                      <span className="pill-action" role="button" tabIndex={0} title={t('certConvertHint')}>
                        {busy ? <LoadingOutlined /> : `${t('certChange')} ▾`}
                      </span>
                    </Dropdown>
                  );
                })()
              )}
            </div>
          )}
          <div className="row" style={{ gap: 6, marginTop: 4 }}>
            {integ ? (
              (() => {
                const badgeClass =
                  integ.errorCount > 0
                    ? 'status error'
                    : integ.warningCount > 0
                      ? 'status warn'
                      : 'status ok';
                const items = integ.items ?? [];
                // Clickable only when there's something to explain (issues present
                // AND the server sent the item detail). A clean disk, or an older
                // stored session without item detail, keeps the plain tooltip badge.
                const clickable = items.length > 0;
                const label = (
                  <span className={badgeClass} style={clickable ? { cursor: 'pointer' } : undefined}>
                    {integ.errorCount}e/{integ.warningCount}w
                  </span>
                );
                // The table is sticky + horizontally scrollable; a body-portaled
                // popup can't align to a trigger inside that context and lands at
                // (0,0) then closes. Anchor the popover to the table's scroll
                // content instead: it contains every row (so the popup paints
                // ABOVE the rows, not clipped by the cell below), its vertical
                // overflow is visible, and it scrolls with the content. Fall back
                // up the chain, then to body. The hover Tooltip ("click for
                // details") is nested inside so hovering still hints — with the
                // position fixed it no longer conflicts with the click.
                return clickable ? (
                  <Popover
                    trigger="click"
                    placement="rightTop"
                    getPopupContainer={(node) =>
                      node.closest('.ant-table-content') ??
                      node.closest('.ant-table-body') ??
                      node.closest('.ant-table') ??
                      document.body
                    }
                    title={t('diskIntegrityDetailTitle')}
                    content={
                      <div style={{ maxWidth: 380, maxHeight: 300, overflowY: 'auto' }}>
                        {items.map((it, i) => (
                          <div
                            key={i}
                            style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 6 }}
                          >
                            <span
                              className={it.level === 'error' ? 'status error' : 'status warn'}
                              style={{ flex: '0 0 auto' }}
                            >
                              {it.level === 'error' ? t('integrityError') : t('integrityWarning')}
                            </span>
                            <span style={{ fontSize: 12 }}>
                              <strong>{it.category}:</strong> {it.message}
                            </span>
                          </div>
                        ))}
                      </div>
                    }
                  >
                    <Tooltip title={t('diskIntegrityClickHint')}>{label}</Tooltip>
                  </Popover>
                ) : (
                  <Tooltip
                    title={t('diskIntegrityHint', { e: integ.errorCount, w: integ.warningCount })}
                  >
                    {label}
                  </Tooltip>
                );
              })()
            ) : (
              <Tooltip title={t('diskIntegrityNaHint')}>
                <span className="status muted">
                  n/a
                </span>
              </Tooltip>
            )}
          </div>
        </div>
      );
    },
  };

  const barcodeCol: ColumnsType<DiskItem>[number] = {
    title: colHeader(t('diskBarcode'), t('diskBarcodeDate')),
    key: 'barcode',
    width: 200,
    render: (_: unknown, d: DiskItem) => {
      const sum = d.summary;
      const isManual = manualBc.has(d.serial);
      const isSyncDate = syncDate.has(d.serial);
      const isSyncBom = syncBom.has(d.serial);
      const bcValue = effectiveBarcode(d.serial);
      const bcDate = bcValue ? decodeDateCode(parseBarcode(bcValue).dateCode) : '';
      const bcWarnings = bcValue ? validateBarcode(parseBarcode(bcValue)) : [];
      const bcKeyWarn = elabelFieldWarning('barcode', bcValue, sum?.barcode ?? '');
      return (
        <div style={{ minWidth: 170 }}>
          <Input
            size="small"
            value={bcValue}
            disabled={!isManual || !editable}
            status={bcKeyWarn ? 'warning' : undefined}
            onChange={(e) => setEdit(d.serial, 'barcode', e.target.value)}
            style={{ fontFamily: 'monospace' }}
          />
          {bcKeyWarn && (
            <div className="barcode-warn" style={{ fontSize: 10 }}>
              ⚠ {t(bcKeyWarn.tKey, bcKeyWarn.params)}
            </div>
          )}
          <div className="row" style={{ gap: 8, marginTop: 2 }}>
            <label className="muted" style={{ cursor: editable ? 'pointer' : 'not-allowed', fontSize: 10 }}>
              <input
                type="checkbox"
                checked={isManual}
                disabled={!editable}
                onChange={() => toggleManual(d.serial)}
                style={{ marginRight: 3, verticalAlign: 'middle' }}
              />
              {t('barcodeManual')}
            </label>
            <Tooltip title={t('barcodeSyncDateHint')}>
              <label
                className="muted"
                style={{ cursor: isManual || !editable ? 'not-allowed' : 'pointer', fontSize: 10, opacity: isManual || !editable ? 0.5 : 1 }}
              >
                <input
                  type="checkbox"
                  checked={isSyncDate}
                  disabled={isManual || !editable}
                  onChange={() => toggleSyncDate(d.serial)}
                  style={{ marginRight: 3, verticalAlign: 'middle' }}
                />
                {t('barcodeSyncDate')}
              </label>
            </Tooltip>
            <Tooltip title={t('barcodeSyncBomHint')}>
              <label
                className="muted"
                style={{ cursor: isManual || !editable ? 'not-allowed' : 'pointer', fontSize: 10, opacity: isManual || !editable ? 0.5 : 1 }}
              >
                <input
                  type="checkbox"
                  checked={isSyncBom}
                  disabled={isManual || !editable}
                  onChange={() => toggleSyncBom(d.serial)}
                  style={{ marginRight: 3, verticalAlign: 'middle' }}
                />
                {t('barcodeSyncBom')}
              </label>
            </Tooltip>
          </div>
          {bcValue !== (sum?.barcode ?? '') && (
            <div style={{ fontSize: 10 }}>
              <span className="diff-old">{sum?.barcode || '(empty)'}</span>{' '}
              <span className="diff-new">→ {bcValue || '(empty)'}</span>
            </div>
          )}
          {bcDate && (
            <div className="muted" style={{ fontSize: 10 }}>
              {t('diskBarcodeDate')}: {bcDate}
            </div>
          )}
          {bcWarnings.length > 0 && (
            <Tooltip title={t('diskBarcodeInvalidTitle')}>
              <div className="barcode-warn" style={{ fontSize: 10 }}>
                ⚠ {t('diskBarcodeInvalid')}: {bcWarnings.join('; ')}
              </div>
            </Tooltip>
          )}
        </div>
      );
    },
  };

  // Explicit narrow widths keep the whole row on one screen. Model/Description
  // are the only flexible ones (their editors wrap within the given width).
  const colWidths: Record<PlainField, number> = {
    bom: 120,
    level: 64,
    manufactured: 120,
    boardType: 130,
    model: 200,
    description: 220,
  };

  // One field's inline editor (+ the BOM length warning). Shared by the plain
  // single-field column and the stacked column-group cell below.
  const renderFieldEditor = (
    d: DiskItem,
    key: PlainField,
    kind: 'text' | 'date' | 'multiline',
  ): React.ReactNode => {
    const ed = edits[d.serial] ?? {};
    const sum = d.summary;
    const value = ed[key] ?? sum?.[key] ?? '';
    // BOM must be exactly 8 chars to fit the BarCode material slot; a light ⚠
    // explains why "sync BOM" won't apply a wrong-length value.
    const bomBad = key === 'bom' && value.length > 0 && value.length !== 8;
    const w = elabelFieldWarning(key, value, sum?.[key] ?? '');
    return (
      <>
        <CellEditor
          multiline={kind === 'multiline'}
          type={kind === 'date' ? 'date' : 'text'}
          disabled={!editable}
          value={value}
          original={sum?.[key] ?? ''}
          warn={w ? t(w.tKey, w.params) : null}
          onChange={(v) => setEdit(d.serial, key, v)}
        />
        {bomBad && (
          <Tooltip title={t('bomLenHint')}>
            <div className="barcode-warn" style={{ fontSize: 10 }}>
              ⚠ {t('bomLenWarn', { n: value.length })}
            </div>
          </Tooltip>
        )}
      </>
    );
  };

  const fieldColumn = (
    key: PlainField,
    label: string,
    kind: 'text' | 'date' | 'multiline',
  ): ColumnsType<DiskItem>[number] => ({
    title: label,
    key,
    width: colWidths[key],
    render: (_: unknown, d: DiskItem) => <div>{renderFieldEditor(d, key, kind)}</div>,
  });

  // A column GROUP: two fields packed into one physical column, stacked
  // vertically (top field above a dashed divider, bottom field below), with the
  // two labels stacked the same way in the header. Saves horizontal width.
  const stackedFieldColumn = (
    a: { key: PlainField; label: string; kind: 'text' | 'date' | 'multiline' },
    b: { key: PlainField; label: string; kind: 'text' | 'date' | 'multiline' },
    width?: number,
  ): ColumnsType<DiskItem>[number] => ({
    key: `${a.key}__${b.key}`,
    width: width ?? Math.max(colWidths[a.key], colWidths[b.key]),
    title: (
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
        <span>{a.label}</span>
        <span style={{ marginTop: 2 }}>{b.label}</span>
      </div>
    ),
    render: (_: unknown, d: DiskItem) => (
      <div>
        <div style={{ fontSize: 10, opacity: 0.55 }}>{a.label}</div>
        {renderFieldEditor(d, a.key, a.kind)}
        <div
          style={{
            marginTop: 8,
            paddingTop: 6,
            borderTop: '1px dashed var(--ant-color-border, #d9d9d9)',
          }}
        >
          <div style={{ fontSize: 10, opacity: 0.55 }}>{b.label}</div>
          {renderFieldEditor(d, b.key, b.kind)}
        </div>
      </div>
    ),
  });

  // Warn when a whitelist list value carries a char that is unsafe in the blob
  // (spaces/quotes/$/`/\) or a structural delimiter (comma/semicolon). Pipe is OK.
  const wlValueWarn = (v: string): boolean => /[ "'$`\\,;]/.test(v);

  // BOM column, extended with the disk Whitelist editor in the free vertical
  // space beneath it (the list type/version are editable; the whitelist BOM is
  // shown as a small reference and tracks the BOM field above on Apply; the blob
  // `version` is never edited). Rendered only for disks that already carry a
  // whitelist — a disk without one is never given one.
  const bomCol: ColumnsType<DiskItem>[number] = {
    // Header mirrors the cell: top row labels "BOM" and "Level" over their two
    // side-by-side fields, then "Whitelist" for the section beneath.
    title: (
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <span style={{ flex: 1, minWidth: 0 }}>{t('diskBom')}</span>
          <span style={{ width: 60, flexShrink: 0 }}>{t('diskLevel')}</span>
        </div>
        <span style={{ marginTop: 2 }}>{t('wlSectionTitle')}</span>
      </div>
    ),
    key: 'bom',
    width: 230,
    render: (_: unknown, d: DiskItem) => {
      const ed = edits[d.serial] ?? {};
      const sum = d.summary;
      const value = ed.bom ?? sum?.bom ?? '';
      const bomBad = value.length > 0 && value.length !== 8;
      const w = elabelFieldWarning('bom', value, sum?.bom ?? '');
      // Level is packed into the same column, horizontally beside BOM.
      const level = ed.level ?? sum?.level ?? '';
      const levelW = elabelFieldWarning('level', level, sum?.level ?? '');
      const wl = d.whitelist;
      const wlType = ed.wlType ?? wl?.listType ?? '';
      const wlVersion = ed.wlVersion ?? wl?.listVersion ?? '';
      // Reference: the whitelist's OWN stored BOM. On Apply it is re-synced to the
      // BOM field above, so when the effective BOM differs we show "stored → new".
      const wlStoredBom = wl?.bom ?? '';
      const wlEffectiveBom = value || wlStoredBom;
      const wlBomChanges = wlEffectiveBom !== wlStoredBom;
      return (
        <div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <CellEditor
                type="text"
                disabled={!editable}
                value={value}
                original={sum?.bom ?? ''}
                warn={w ? t(w.tKey, w.params) : null}
                onChange={(v) => setEdit(d.serial, 'bom', v)}
              />
              {bomBad && (
                <Tooltip title={t('bomLenHint')}>
                  <div className="barcode-warn" style={{ fontSize: 10 }}>
                    ⚠ {t('bomLenWarn', { n: value.length })}
                  </div>
                </Tooltip>
              )}
            </div>
            <div style={{ width: 60, flexShrink: 0 }}>
              <CellEditor
                type="text"
                disabled={!editable}
                value={level}
                original={sum?.level ?? ''}
                warn={levelW ? t(levelW.tKey, levelW.params) : null}
                onChange={(v) => setEdit(d.serial, 'level', v)}
              />
            </div>
          </div>
          {wl && (
            <div
              style={{
                marginTop: 8,
                paddingTop: 6,
                borderTop: '1px dashed var(--ant-color-border, #d9d9d9)',
              }}
            >
              {/* No "Whitelist" header — the dashed divider + labels are enough.
                  The hint rides on the List type label. List type + version sit
                  side by side (left → right). */}
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Tooltip title={t('wlHint')}>
                    <div style={{ fontSize: 10, opacity: 0.6, cursor: 'help' }}>{t('wlType')}</div>
                  </Tooltip>
                  <CellEditor
                    type="text"
                    disabled={!editable}
                    value={wlType}
                    original={wl.listType}
                    warn={wlValueWarn(wlType) ? t('wlValueWarn') : null}
                    onChange={(v) => setEdit(d.serial, 'wlType', v)}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10, opacity: 0.6 }}>{t('wlVersion')}</div>
                  <CellEditor
                    type="text"
                    disabled={!editable}
                    value={wlVersion}
                    original={wl.listVersion}
                    warn={wlValueWarn(wlVersion) ? t('wlValueWarn') : null}
                    onChange={(v) => setEdit(d.serial, 'wlVersion', v)}
                  />
                </div>
              </div>
              {wlBomChanges ? (
                // The whitelist BOM will be re-synced — let the operator opt out
                // of just this write with a checkbox (label on the left).
                <div style={{ marginTop: 3 }}>
                  <Checkbox
                    checked={!isFieldExcluded(d.serial, 'wlBom')}
                    disabled={!editable}
                    onChange={() => toggleFieldExcluded(d.serial, 'wlBom')}
                  >
                    <span style={{ fontSize: 10 }}>
                      {t('wlBomRef')}: <code style={{ fontSize: 10 }}>{wlStoredBom || '—'}</code>{' '}
                      {isFieldExcluded(d.serial, 'wlBom') ? (
                        <span className="muted" style={{ textDecoration: 'line-through' }}>
                          → {wlEffectiveBom || '(empty)'}
                        </span>
                      ) : (
                        <span className="diff-new">→ {wlEffectiveBom || '(empty)'}</span>
                      )}
                    </span>
                  </Checkbox>
                </div>
              ) : (
                <div style={{ fontSize: 10, opacity: 0.55, marginTop: 3 }}>
                  {t('wlBomRef')}: <code style={{ fontSize: 10 }}>{wlStoredBom || '—'}</code>
                </div>
              )}
            </div>
          )}
        </div>
      );
    },
  };

  const actionsCol: ColumnsType<DiskItem>[number] = {
    title: t('colActions'),
    key: 'actions',
    fixed: 'right',
    width: 108,
    // 3 per row → the six icon actions land as two tidy rows instead of one long
    // strip (a client sees only the first two, i.e. a single row).
    render: (_: unknown, d: DiskItem) => (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, min-content)',
          gap: 4,
          justifyContent: 'start',
        }}
      >
        <Tooltip title={t('undoTooltip')}>
          <Button
            size="small"
            icon={<UndoOutlined />}
            disabled={!editable || !undoableSerials.has(d.serial)}
            onClick={() => undoForSerial(d.serial)}
          />
        </Tooltip>
        <Tooltip title={t('buttonHistory')}>
          <Button
            size="small"
            icon={<HistoryOutlined />}
            onClick={() => setHistoryForSn(d.serial)}
          />
        </Tooltip>
        {isAdminRole(user.role) && (
          <Tooltip title={t('buttonHex')}>
            <Button size="small" icon={<EyeOutlined />} onClick={() => setHexForSn(d.serial)} />
          </Tooltip>
        )}
        {isAdminRole(user.role) && (
          <Tooltip title={t('hwrsvExportHint')}>
            <Button size="small" icon={<DownloadOutlined />} onClick={() => exportDisk(d.serial)} />
          </Tooltip>
        )}
        {isAdminRole(user.role) && (
          <Tooltip title={t('hwrsvImportHint')}>
            <Button size="small" icon={<UploadOutlined />} onClick={() => void importDisk(d.serial)} />
          </Tooltip>
        )}
        {isAdminRole(user.role) && (
          <Tooltip title={t('pcCycleDiskHint')}>
            <Button
              size="small"
              danger
              icon={<ThunderboltOutlined />}
              loading={powercycling !== null}
              onClick={() => confirmPowercycle([d.serial])}
            />
          </Tooltip>
        )}
      </div>
    ),
  };

  const columns: ColumnsType<DiskItem> = [
    // Frame + Slot + Type are folded into the Order column (frame/slot lines +
    // transport badge) to save horizontal space. Slot-number SORTING is
    // unaffected (it's driven by the sort control, not a column). Every column is
    // always shown — column hiding was removed; horizontal space is instead saved
    // by packing related fields into stacked column-groups.
    orderCol,
    serialCol,
    bomCol, // BOM + Level (side by side) + the whitelist editor beneath
    barcodeCol,
    // Manufactured + BoardType share one column, stacked vertically.
    stackedFieldColumn(
      { key: 'manufactured', label: t('diskManufactured'), kind: 'date' },
      { key: 'boardType', label: t('diskBoardType'), kind: 'text' },
    ),
    // Model + Description share one column, stacked vertically — the freed
    // horizontal space widens Description (long OceanStor descriptions wrap far
    // more legibly).
    stackedFieldColumn(
      { key: 'model', label: t('diskModel'), kind: 'multiline' },
      { key: 'description', label: t('diskDescription'), kind: 'multiline' },
      380,
    ),
    actionsCol,
  ].map((c) => (isNarrow ? { ...c, fixed: undefined } : c));

  return (
    <div>
      <QuotaBanner role={user.role} />
      {staleFlashBy && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('editorStaleFlashTitle', { user: staleFlashBy })}
          description={t('editorStaleFlashBody')}
          action={
            <Button size="small" onClick={reload} loading={reloading}>
              {t('editorReloadBtn')}
            </Button>
          }
        />
      )}
      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'stretch',
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        {leftPanel && (
          <div style={{ flex: '1 1 calc(40% - 8px)', minWidth: 260 }}>{leftPanel}</div>
        )}
        <div className="card" style={{ flex: '1 1 calc(60% - 8px)', minWidth: 300, marginBottom: 0 }}>
        <div className="spread">
          <div>
            <h2>{t('sessionEditorTitle')}</h2>
            <div className="muted">
              {t('editorDevice')}:{' '}
              <strong>
                {devicesQ.data?.find((d) => d.id === s.deviceId)?.label || s.deviceId.substring(0, 8)}
              </strong>{' '}
              · {s.diskCount} disks
            </div>
          </div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Tooltip title={t('undoTooltip')}>
              <Button
                icon={<UndoOutlined />}
                disabled={history.length === 0 || !editable}
                onClick={undoGlobal}
              >
                {t('undoButton')}
              </Button>
            </Tooltip>
            <Popconfirm title={t('editorReloadConfirm')} onConfirm={reload} disabled={!hasEdits}>
              <Button
                icon={<ReloadOutlined />}
                loading={reloading}
                onClick={hasEdits ? undefined : reload}
              >
                {t('editorReload')}
              </Button>
            </Popconfirm>
            <Tooltip title={t('exportXlsTip')}>
              <Button
                icon={<DownloadOutlined />}
                disabled={displayDisks.length === 0}
                onClick={exportXls}
              >
                {t('exportXls')}
              </Button>
            </Tooltip>
            <Button
              disabled={!hasPendingChanges || preview.isPending || !editable}
              loading={preview.isPending}
              onClick={() => preview.mutate()}
            >
              {t('previewBtn')}
            </Button>
            <Tooltip title={quotaShort ? t('quotaShortTitle') : undefined}>
              <Button
                type="primary"
                disabled={!hasPendingChanges || apply.isPending || !editable || quotaShort || flashCount === 0}
                loading={apply.isPending}
                onClick={() => setConfirmOpen(true)}
              >
                {t('buttonApply')}
              </Button>
            </Tooltip>
          </div>
        </div>
        {user.role === 'client' && editable && flashCount > 0 && (
          <Alert
            style={{ marginTop: 12 }}
            type={quotaShort ? 'error' : flashCount === quotaAvailable ? 'warning' : 'info'}
            showIcon
            message={t('quotaFlashCount', { n: flashCount, available: quotaAvailable })}
            description={quotaShort ? t('quotaShortHint', { available: quotaAvailable }) : undefined}
          />
        )}
        {applying && (
          <Alert
            style={{ marginTop: 12 }}
            type="info"
            showIcon
            icon={<Spin size="small" />}
            message={t('editorApplying')}
            description={t('editorApplyingHint')}
          />
        )}
        {powercycling && (
          <Alert
            style={{ marginTop: 12 }}
            type="warning"
            showIcon
            icon={<Spin size="small" />}
            message={t('pcWaiting', { serials: powercycling.join(', ') })}
            description={t('pcWaitingHint')}
          />
        )}
        {confirmResults && !flashDone && (
          <Alert
            style={{ marginTop: 12 }}
            type={confirmResults.every((r) => r.status === 'OK') ? 'success' : 'error'}
            showIcon
            message={
              confirmResults.every((r) => r.status === 'OK')
                ? t('editorApplyOk')
                : t('editorApplyFailed')
            }
            description={
              <div>
                <div style={{ marginBottom: 4 }}>
                  {t('editorApplyCount', {
                    flashed: confirmResults.length,
                    unchanged: Math.max(0, s.diskCount - confirmResults.length),
                  })}
                </div>
                {confirmResults.map((r, i) => (
                  <div key={i} style={{ marginTop: 2 }}>
                    <Tag color={r.status === 'OK' ? 'green' : 'red'}>{r.status}</Tag>
                    <code>{r.serial}</code>
                    {r.powercycle === 'ok' && (
                      <Tag color="blue" style={{ marginLeft: 6 }}>
                        ⚡ {t('pcBackInSec', { sec: r.returnedSec ?? 0 })}
                      </Tag>
                    )}
                    {r.powercycle === 'timeout' && (
                      <Tag color="orange" style={{ marginLeft: 6 }}>⚡ {t('pcNoReturn')}</Tag>
                    )}
                    {r.powercycle === 'failed' && (
                      <Tag color="red" style={{ marginLeft: 6 }}>⚡ {t('pcFailed')}</Tag>
                    )}
                    {r.powercycle === 'skipped' && (
                      <Tag style={{ marginLeft: 6 }}>{t('pcSkipped')}</Tag>
                    )}
                    {r.detail ? ` — ${r.detail}` : ''}
                  </div>
                ))}
                {isAdminRole(user.role) && (
                  <div style={{ marginTop: 8 }}>
                    <Button
                      size="small"
                      icon={<DownloadOutlined />}
                      href={`/api/admin/v1/sessions/${id}/backup.hwrsv`}
                      download
                    >
                      {t('downloadGroupBackup')}
                    </Button>
                  </div>
                )}
              </div>
            }
          />
        )}
        {/* Sticky success banner — set at flash-confirm, survives the auto-rescan
            jump, and is dismissed only by the operator (close ×). */}
        {flashDone && (
          <Alert
            style={{ marginTop: 12 }}
            type={flashDone.results.every((r) => r.status === 'OK') ? 'success' : 'error'}
            showIcon
            closable
            onClose={() => setFlashDone(null)}
            message={
              flashDone.results.every((r) => r.status === 'OK')
                ? t('editorApplyOk')
                : t('editorApplyFailed')
            }
            description={
              <div>
                <div style={{ marginBottom: 4 }}>
                  {t('editorApplyCount', {
                    flashed: flashDone.results.length,
                    unchanged: Math.max(0, flashDone.diskCount - flashDone.results.length),
                  })}
                </div>
                {flashDone.results.map((r, i) => (
                  <div key={i} style={{ marginTop: 2 }}>
                    <Tag color={r.status === 'OK' ? 'green' : 'red'}>{r.status}</Tag>
                    <code>{r.serial}</code>
                    {r.powercycle === 'ok' && (
                      <Tag color="blue" style={{ marginLeft: 6 }}>
                        ⚡ {t('pcBackInSec', { sec: r.returnedSec ?? 0 })}
                      </Tag>
                    )}
                    {r.powercycle === 'timeout' && (
                      <Tag color="orange" style={{ marginLeft: 6 }}>⚡ {t('pcNoReturn')}</Tag>
                    )}
                    {r.powercycle === 'failed' && (
                      <Tag color="red" style={{ marginLeft: 6 }}>⚡ {t('pcFailed')}</Tag>
                    )}
                    {r.powercycle === 'skipped' && (
                      <Tag style={{ marginLeft: 6 }}>{t('pcSkipped')}</Tag>
                    )}
                    {r.detail ? ` — ${r.detail}` : ''}
                  </div>
                ))}
                {isAdminRole(user.role) && (
                  <div style={{ marginTop: 8 }}>
                    <Button
                      size="small"
                      icon={<DownloadOutlined />}
                      href={`/api/admin/v1/sessions/${flashDone.sessionId}/backup.hwrsv`}
                      download
                    >
                      {t('downloadGroupBackup')}
                    </Button>
                  </div>
                )}
              </div>
            }
          />
        )}
        {postApplyWaiting && (
          <Alert
            style={{ marginTop: 12 }}
            type="info"
            showIcon
            icon={<Spin size="small" />}
            message={t('editorPostApplyWaiting')}
            description={t('editorPostApplyWaitingHint')}
          />
        )}
        {applyMsg && (
          <Alert
            style={{ marginTop: 12 }}
            type={applyMsg.ok ? 'success' : 'error'}
            showIcon
            message={applyMsg.text}
          />
        )}
        {previewResult && (
          <div className="preview-panel" style={{ marginTop: 12, padding: 12, borderRadius: 4, fontSize: 13 }}>
            <div className="spread" style={{ marginBottom: 8 }}>
              <strong>{t('previewTitle')}</strong>
              <button className="secondary" onClick={() => setPreviewResult(null)} style={{ fontSize: 11 }}>
                ×
              </button>
            </div>
            {previewResult.disks.map((d) => (
              <div key={d.serial} style={{ marginBottom: 10 }}>
                <div style={{ marginBottom: 4 }}>
                  <code>{d.serial}</code> ·{' '}
                  <span className="muted">
                    {t('previewBytesChanged')}: <strong>{d.totalBytesChanged}</strong>
                  </span>
                </div>
                {d.outcome.applied.length > 0 && (
                  <ul style={{ margin: '4px 0 4px 20px', fontSize: 12 }}>
                    {d.outcome.applied.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                )}
                {d.outcome.rejected.length > 0 && (
                  <ul style={{ margin: '4px 0 4px 20px', fontSize: 12, color: '#c9302c' }}>
                    {d.outcome.rejected.map((r, i) => (
                      <li key={i}>
                        <code>{r.change}</code>: {r.reason}
                      </li>
                    ))}
                  </ul>
                )}
                {d.regions.length > 0 && (
                  <table style={{ fontSize: 11, marginTop: 4 }}>
                    <thead>
                      <tr>
                        <th>id</th>
                        <th>{t('previewRegion')}</th>
                        <th>{t('previewBytesChanged')}</th>
                        <th>{t('previewFirstOffset')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.regions.map((r) => (
                        <tr key={r.id}>
                          <td>r{r.id}</td>
                          <td>{r.name}</td>
                          <td>{r.bytesChanged}</td>
                          <td>
                            <code>0x{r.firstChangeOffset.toString(16)}</code>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        )}
        </div>
      </div>

      <div className="card">
        {/* Only the editable field inputs + bulk-fill/counter/Apply are gated on
            `editable`; inspection actions (History, Hex, export, power-cycle, row
            selection, sort) stay usable on confirmed/failed sessions so an admin
            can still diagnose. */}
        <div style={{ minInlineSize: 0 }}>
          {/* Toolbar row 1: selection + bulk-fill + column visibility */}
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <Button size="small" onClick={() => setSelected(new Set(s.disks.map((d) => d.serial)))}>
              {t('batchSelectAll')}
            </Button>
            <Button size="small" disabled={selected.size === 0} onClick={() => setSelected(new Set())}>
              {t('batchClear')}
            </Button>
            <span className="muted" style={{ fontSize: 12 }}>
              {t('batchSelected', { n: selected.size })}
            </span>
            <Divider type="vertical" />
            <span className="muted" style={{ fontSize: 12 }}>
              {t('bulkFillTitle')}:
            </span>
            <Select
              size="small"
              style={{ width: 150 }}
              value={bulkField}
              disabled={!editable}
              onChange={(v) => setBulkField(v)}
              options={[
                { value: 'bom', label: t('diskBom') },
                { value: 'level', label: t('diskLevel') },
                { value: 'manufactured', label: t('diskManufactured') },
                { value: 'boardType', label: t('diskBoardType') },
                { value: 'model', label: t('diskModel') },
                { value: 'description', label: t('diskDescription') },
              ]}
            />
            {bulkField === 'manufactured' ? (
              <DatePicker
                size="small"
                disabled={!editable}
                value={bulkValue ? dayjs(bulkValue) : null}
                onChange={(d) => setBulkValue(d ? d.format('YYYY-MM-DD') : '')}
              />
            ) : (
              <Input
                size="small"
                style={{ width: 170 }}
                placeholder={t('bulkFillValue')}
                disabled={!editable}
                value={bulkValue}
                onChange={(e) => setBulkValue(e.target.value)}
              />
            )}
            <Button
              size="small"
              type="primary"
              disabled={!bulkValue || selected.size === 0 || !editable}
              onClick={() => bulkFill(bulkField, bulkValue)}
            >
              {t('bulkFillApply', { n: selected.size })}
            </Button>
            {isAdminRole(user.role) && (
              <>
                <Divider type="vertical" />
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  disabled={selected.size === 0}
                  onClick={exportSelected}
                >
                  {t('hwrsvExportSelected')}
                </Button>
                <Tooltip title={t('hwrsvImportCombinedHint')}>
                  <Button size="small" icon={<UploadOutlined />} onClick={() => void importCombined()}>
                    {t('hwrsvImportCombined')}
                  </Button>
                </Tooltip>
                {/* Bulk certificate change over the ticked disks. */}
                <Dropdown
                  trigger={['click']}
                  disabled={selectedSerials.length === 0 || !editable || certConvertBulk.isPending}
                  menu={{
                    items: [
                      ...signableCas.map((c) => ({
                        key: `ca:${c.id}`,
                        label: t('certConvertTo', { name: c.name }),
                      })),
                      { key: 'orig', label: t('certRestoreOriginal') },
                    ],
                    onClick: ({ key }) => {
                      const to = key === 'orig' ? 'orig' : 'ca';
                      const caId = key === 'orig' ? undefined : key.slice(3);
                      const target =
                        key === 'orig'
                          ? t('certRestoreOriginal')
                          : signableCas.find((c) => `ca:${c.id}` === key)?.name ?? '';
                      modal.confirm({
                        title: t('certBulkConfirm', { n: selectedSerials.length, target }),
                        okText: t('certConvertOk'),
                        cancelText: t('cancel'),
                        onOk: () => certConvertBulk.mutate({ to, caId }),
                      });
                    },
                  }}
                >
                  <Button
                    size="small"
                    icon={<SafetyCertificateOutlined />}
                    loading={certConvertBulk.isPending}
                    disabled={selectedSerials.length === 0}
                  >
                    {t('certChangeSelected')} <DownOutlined style={{ fontSize: 10 }} />
                  </Button>
                </Dropdown>
                <Divider type="vertical" />
                <Tooltip title={t('pcCycleSelectedHint')}>
                  <Button
                    size="small"
                    danger
                    icon={<ThunderboltOutlined />}
                    loading={powercycling !== null}
                    disabled={selectedSerials.length === 0}
                    onClick={() => confirmPowercycle(selectedSerials)}
                  >
                    {t('pcCycleSelected')}
                  </Button>
                </Tooltip>
              </>
            )}
          </div>

          {/* Toolbar row 2: sort + BarCode counter */}
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
            <span className="muted" style={{ fontSize: 12 }}>
              {t('sortBy')}:
            </span>
            <Select
              size="small"
              style={{ width: 160 }}
              value={sortField}
              onChange={(v) => setSortField(v)}
              options={[
                { value: 'slot', label: t('sortSlot') },
                { value: 'id', label: t('sortId') },
                { value: 'sn', label: t('sortSn') },
                { value: 'barcode', label: t('sortBarcode') },
                { value: 'manual', label: t('sortManual') },
              ]}
            />
            <Checkbox
              checked={sortDesc}
              disabled={sortField === 'manual'}
              onChange={(e) => setSortDesc(e.target.checked)}
            >
              {t('sortDesc')}
            </Checkbox>
            {sortField === 'manual' && (
              <span className="muted" style={{ fontSize: 12 }}>
                {t('reorderManualHint')}
              </span>
            )}
            <Divider type="vertical" />
            <Tooltip title={t('batchBarcodeHint')}>
              <span className="muted" style={{ fontSize: 12 }}>
                {t('batchBarcodeTitle')}:
              </span>
            </Tooltip>
            <Input
              size="small"
              maxLength={6}
              placeholder={t('batchBarcodeStart')}
              disabled={!editable}
              value={bcStart}
              onChange={(e) => {
                setBcStart(e.target.value);
                setBcStartTouched(true);
              }}
              style={{ width: 140, fontFamily: 'monospace' }}
            />
            <Button
              size="small"
              disabled={selected.size === 0 || !/\d/.test(bcStart) || !editable}
              onClick={runBarcodeCounter}
            >
              {t('barcodeCounterApply', { n: selected.size })}
            </Button>
            <span className="muted" style={{ fontSize: 12 }}>
              {t('batchBarcodeSrcHint')}
            </span>
            {batchMsg && (
              <span className="muted" style={{ fontSize: 12 }}>
                {batchMsg}
              </span>
            )}
          </div>

          <Table<DiskItem>
            className="disk-table"
            size="small"
            rowKey="serial"
            columns={columns}
            dataSource={displayDisks}
            pagination={false}
            scroll={{ x: 'max-content' }}
            /* Keep the header AND the horizontal scrollbar stuck to the viewport
             * so on a tall table (mobile) the x-scrollbar doesn't end up below the
             * fold — otherwise only the fixed columns are reachable. */
            sticky={{ offsetHeader: 0, offsetScroll: 0 }}
            rowClassName={rowClassName}
            onRow={(record) => ({
              onDragOver: (e) => {
                if (sortField === 'manual' && dragSn.current) {
                  e.preventDefault();
                  if (dragOverSn !== record.serial) setDragOverSn(record.serial);
                }
              },
              onDrop: (e) => {
                if (sortField !== 'manual' || !dragSn.current) return;
                e.preventDefault();
                reorderTo(dragSn.current, record.serial);
                dragSn.current = null;
                setDraggingSn(null);
                setDragOverSn(null);
              },
            })}
            rowSelection={{
              fixed: isNarrow ? undefined : 'left',
              selectedRowKeys: [...selected],
              onChange: (keys) => setSelected(new Set(keys as string[])),
              onSelect: (record) => toggleSelect(record.serial),
              columnWidth: 40,
              // The "modified" flag lives here, under the checkbox, as a compact
              // amber pencil (it used to be a pill in the Serial column).
              renderCell: (_v, record, _i, node) => (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  {node}
                  {isModified(record.serial) && (
                    <Tooltip title={t('diskModified')}>
                      <EditOutlined style={{ color: 'var(--warn)', fontSize: 14 }} />
                    </Tooltip>
                  )}
                </div>
              ),
            }}
            locale={{ emptyText: t('emptyList') }}
          />
        </div>
      </div>

      {historyForSn && (
        <BackupHistoryModal
          sn={historyForSn}
          sessionId={s.id}
          canRestore={isAdminRole(user.role)}
          isAdmin={isAdminRole(user.role)}
          isSuper={user.role === 'superadmin'}
          onClose={() => setHistoryForSn(null)}
        />
      )}

      {isAdminRole(user.role) && (
        <Drawer
          open={!!hexForSn}
          width={760}
          title={hexForSn ? t('hexDrawerTitle', { sn: hexForSn }) : ''}
          onClose={() => setHexForSn(null)}
          destroyOnClose
        >
          {hexForSn && (
            <HexViewer
              sessionId={s.id}
              serial={hexForSn}
              transport={diskBySn.get(hexForSn)?.summary?.transport ?? null}
            />
          )}
        </Drawer>
      )}

      {confirmOpen && (
        <ConfirmApplyModal
          rows={buildChangeRows()}
          isExcluded={(sn, fid) => isFieldExcluded(sn, fid)}
          onToggle={(sn, fid) => toggleFieldExcluded(sn, fid)}
          pending={apply.isPending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={(powerCycleAfter) => apply.mutate(powerCycleAfter)}
        />
      )}
    </div>
  );
}

function ConfirmApplyModal({
  rows,
  isExcluded,
  onToggle,
  pending,
  onCancel,
  onConfirm,
}: {
  rows: Array<{ sn: string; fieldId: string; field: string; oldVal: string; newVal: string }>;
  isExcluded: (sn: string, fieldId: string) => boolean;
  onToggle: (sn: string, fieldId: string) => void;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (powerCycleAfter: boolean) => void;
}) {
  const [confirmText, setConfirmText] = useState('');
  // Default ON: after flashing, power-cycle each disk so the controller reloads
  // the Reserve Area and picks up the new identity (otherwise sddebug/Valid stay
  // stale). The operator can opt out per-apply.
  const [powerCycle, setPowerCycle] = useState(true);
  // Only INCLUDED (checked) rows actually flash — count disks by those.
  const includedRows = rows.filter((r) => !isExcluded(r.sn, r.fieldId));
  const diskCount = new Set(includedRows.map((r) => r.sn)).size;
  const armed = confirmText.trim() === 'YES';
  return (
    <div className="modal-bg" onClick={pending ? undefined : onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{t('confirmApplyTitle')}</h2>
        <p className="muted" style={{ marginBottom: 16 }}>
          {t('confirmApplyIntro')}
        </p>
        {rows.length === 0 ? (
          <div className="muted">{t('confirmApplyEmpty')}</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th>{t('changeDisk')}</th>
                <th>{t('changeField')}</th>
                <th>{t('changeOld')}</th>
                <th>{t('changeNew')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const off = isExcluded(r.sn, r.fieldId);
                return (
                  <tr key={`${r.sn}::${r.fieldId}`} style={off ? { opacity: 0.45 } : undefined}>
                    <td>
                      <Checkbox
                        checked={!off}
                        disabled={pending}
                        onChange={() => onToggle(r.sn, r.fieldId)}
                      />
                    </td>
                    <td>
                      <code style={{ fontSize: 11 }}>{r.sn}</code>
                    </td>
                    <td>{r.field}</td>
                    <td>
                      <span className="diff-old">{r.oldVal}</span>
                    </td>
                    <td>
                      {off ? (
                        <span className="muted" style={{ textDecoration: 'line-through' }}>{r.newVal}</span>
                      ) : (
                        <span className="diff-new">{r.newVal}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {rows.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 14 }}>
              <Checkbox
                checked={powerCycle}
                disabled={pending}
                onChange={(e) => setPowerCycle(e.target.checked)}
              >
                {t('confirmApplyPowerCycle')}
              </Checkbox>
              <div className="muted" style={{ fontSize: 12, marginTop: 4, marginLeft: 24 }}>
                {t('confirmApplyPowerCycleHint')}
              </div>
            </div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>
              {t('confirmApplyTypeYes', { n: diskCount })}
            </label>
            <Input
              autoFocus
              value={confirmText}
              disabled={pending}
              placeholder="YES"
              onChange={(e) => setConfirmText(e.target.value)}
              onPressEnter={() => {
                if (armed && !pending) onConfirm(powerCycle);
              }}
              style={{ maxWidth: 220, fontFamily: 'monospace' }}
            />
          </div>
        )}
        <div className="row" style={{ marginTop: 20, justifyContent: 'flex-end' }}>
          <button className="secondary" onClick={onCancel} disabled={pending}>
            {t('confirmApplyCancel')}
          </button>
          <button
            className="primary"
            onClick={() => onConfirm(powerCycle)}
            disabled={pending || includedRows.length === 0 || !armed}
          >
            {pending ? t('loadingEllipsis') : t('confirmApplyButton')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Edit-field key → its real elabel key name (for the "own key pasted" check).
const ELABEL_FIELD_KEYS: Record<string, string> = {
  model: 'Model',
  description: 'Description',
  boardType: 'BoardType',
  manufactured: 'Manufactured',
  barcode: 'BarCode',
};
// Elabel keys a stray "Key=Value" paste could start with.
const KNOWN_ELABEL_KEYS = [
  'Description',
  'Model',
  'BoardType',
  'BarCode',
  'Manufactured',
  'VendorName',
  'Item',
  'BOM',
];

/**
 * Flag a value that looks like a whole `Key=Value` elabel line was pasted into an
 * edit field (the human-factor cause of on-disk `Description=Description=…`), or
 * that carries a CRLF (which would split the elabel into a phantom field). Pure —
 * returns an i18n key + params so the caller renders it in the active language.
 * NB: this only WARNS; the value is never mutated (the write path is byte-exact).
 */
function elabelFieldWarning(
  field: string,
  value: string,
  original = '',
): {
  tKey: 'fieldSelfKeyWarn' | 'fieldForeignKeyWarn' | 'fieldNewlineWarn' | 'fieldEdgeSpaceWarn';
  params?: Record<string, string>;
} | null {
  if (!value) return null;
  const m = value.match(new RegExp(`^\\s*(${KNOWN_ELABEL_KEYS.join('|')})\\s*=`, 'i'));
  if (m) {
    const pasted = m[1]!;
    const own = ELABEL_FIELD_KEYS[field];
    return own && pasted.toLowerCase() === own.toLowerCase()
      ? { tKey: 'fieldSelfKeyWarn', params: { key: pasted } }
      : { tKey: 'fieldForeignKeyWarn', params: { key: pasted } };
  }
  if (/[\r\n]/.test(value)) return { tKey: 'fieldNewlineWarn' };
  // Leading/trailing spaces the original didn't carry: never introduce edge
  // whitespace that wasn't factory-present (it gets written verbatim to elabel).
  const leadNew = /^\s/.test(value) && !/^\s/.test(original);
  const trailNew = /\s$/.test(value) && !/\s$/.test(original);
  if (leadNew || trailNew) return { tKey: 'fieldEdgeSpaceWarn' };
  return null;
}

function CellEditor({
  value,
  original,
  onChange,
  multiline,
  type,
  disabled,
  warn,
}: {
  value: string;
  original: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  type?: 'text' | 'date';
  disabled?: boolean;
  warn?: string | null;
}) {
  const changed = value !== original;
  // Memoise the dayjs value by its string so its REFERENCE only changes when the
  // date actually changes. Otherwise the parent's periodic re-render (the session
  // polls every few seconds) hands DatePicker a fresh dayjs object each time, and
  // Ant resets the open panel's navigated month/year back to the value — which is
  // why paging months/years "snapped back to today" after a couple of seconds.
  const dayValue = useMemo(() => (value ? dayjs(value) : null), [value]);
  return (
    <div>
      {multiline ? (
        <Tooltip title={value || undefined} placement="topLeft" mouseEnterDelay={1}>
          <Input.TextArea
            size="small"
            value={value}
            disabled={disabled}
            status={warn ? 'warning' : undefined}
            autoSize={{ minRows: 1, maxRows: 4 }}
            onChange={(e) => onChange(e.target.value)}
          />
        </Tooltip>
      ) : type === 'date' ? (
        <DatePicker
          size="small"
          style={{ width: '100%' }}
          disabled={disabled}
          value={dayValue}
          onChange={(d) => onChange(d ? d.format('YYYY-MM-DD') : '')}
        />
      ) : (
        <Input
          size="small"
          value={value}
          disabled={disabled}
          status={warn ? 'warning' : undefined}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {warn && (
        <div className="barcode-warn" style={{ fontSize: 10 }}>
          ⚠ {warn}
        </div>
      )}
      {changed && (
        <div style={{ fontSize: 10 }}>
          <span className="diff-old">{original || '(empty)'}</span>{' '}
          <span className="diff-new">→ {value || '(empty)'}</span>
        </div>
      )}
    </div>
  );
}
